import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { authAxios } from "../../utils/session_utils";
import PdfCanvas from "./PdfCanvas";

const API = process.env.REACT_APP_BACK_END_API || "";
const IS_LOCAL = window.location.hostname === "localhost";

// ─── Action Logger ────────────────────────────────────────────────────
function logTiAction(action: {
	actionType: string;
	category: "navigation" | "edit" | "salesforce" | "system";
	description: string;
	emailId?: string;
	emailSubject?: string;
	dealId?: string;
	trafficId?: string;
	success?: boolean;
	details?: Record<string, any>;
}) {
	authAxios({
		method: "POST",
		url: `${API}/salesforce/traffic-instructions/action-log`,
		data: action,
	}).catch(() => { /* fire-and-forget */ });
}

const GMAIL_EXCLUDE = '-in:trash -from:sendgrid.net -from:mvms-pdfsigner2.appspotmail.com -subject:"MVMS: Order Confirm" -subject:"signed by" -subject:"Parsed"';
function buildDatePresets(): { label: string; q: string }[] {
	const fmt = (d: Date) => `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
	const now = new Date();
	const d7 = new Date(now); d7.setDate(d7.getDate() - 7);
	const d14 = new Date(now); d14.setDate(d14.getDate() - 14);
	const d30 = new Date(now); d30.setDate(d30.getDate() - 30);
	return [
		{ label: "Last 7 days", q: `subject:traffic after:${fmt(d7)} ${GMAIL_EXCLUDE}` },
		{ label: "Last 14 days (1-2 min)", q: `subject:traffic after:${fmt(d14)} ${GMAIL_EXCLUDE}` },
		{ label: "Last 30 days (3-5 min)", q: `subject:traffic after:${fmt(d30)} ${GMAIL_EXCLUDE}` },
	];
}
const DATE_PRESETS = buildDatePresets();

// ─── Types ────────────────────────────────────────────────────────────

interface SpotItem {
	isci: string;
	title: string;
	length: number;
	rot: number;
	tollFreeNumber?: string;
	startDate?: string;
	endDate?: string;
}

interface DaypartGroup {
	daypart: string;
	window: string;
	items: SpotItem[];
}

interface Attachment {
	filename: string;
	mimeType: string;
	attachmentId: string;
	size: number;
}

type InstructionLabel = "new" | "revision" | "cancellation" | "no_change";

interface ComparedItem {
	key: string;
	status: "new" | "unchanged" | "changed" | "cancelled";
	splitId?: string;
	changes?: { field: string; parsed: any; sf: any }[];
}

interface SfComparison {
	dealId: string;
	trafficId: string | null;
	activeSplitCount: number;
	items: ComparedItem[];
	missing: { splitId: string; isci: string; spotName: string; days: string; startTime: string | null; endTime: string | null; startDate: string | null; endDate: string | null; inFlight?: boolean }[];
	plan?: { create: string[]; endDates: { splitId: string; endDate: string }[]; remove: string[] };
	// Cancellations: whether ops entered the LTC (later schedule weeks Canceled) on the deal
	ltc?: { lastAirDate: string | null; status: "done" | "pending" | "no_schedules"; weeksChecked: number; notCanceled: { name: string; week: string; daypart: string; units: number; status: string }[]; checkedAt: string };
	comparedAt: string;
}

interface EmailRecord {
	id: string;
	label: InstructionLabel;
	labelSource: "sf" | "assumed";
	sfComparison?: SfComparison | null;
	subject: string;
	advertiser: string;
	brand: string;
	agency: string;
	from: string;
	size: string;
	network: string;
	campaign: string;
	flight: string;
	flightStart: string;
	flightEnd: string;
	status: "unprocessed" | "processed" | "error";
	unread: boolean;
	received: string;
	receivedShort: string;
	pdfName: string;
	pdfPages: number;
	groups: DaypartGroup[] | null;
	conf: "new" | "ok" | "pushed" | "error";
	attachments: Attachment[];
	body?: string;
	dealMatches?: DealMatch[];
	selectedDealId?: string | null;
	trafficId?: string | null;
	trafficName?: string | null;
	pushCount?: number;
	pdfHash?: string;
	sfSplitsLoaded?: boolean;
}

interface DealMatch {
	dealId: string;
	dealName: string;
	program: string;
	advertiser: string;
	agency: string;
	brand: string;
	campaign: string;
	length: string;
	status: string;
	quarter: string;
	year: number;
	contact: string;
	recordType: string;
}

interface Draft {
	agency: string;
	advertiser: string;
	brand: string;
	campaign: string;
	network: string;
	flightStart: string;
	flightEnd: string;
	rots: Record<string, number>;
	phones: Record<string, string>;
	spotDates: Record<string, { start: string; end: string }>;
}

// ─── Constants ────────────────────────────────────────────────────────

const KIND_META: Record<string, { label: string; color: string; bg: string }> = {
	new: { label: "New Traffic", color: "#0d9488", bg: "#e0f4f1" },
	revision: { label: "Update / Revision", color: "#c98a13", bg: "#fbf1de" },
	cancellation: { label: "Cancellation", color: "#dc3b52", bg: "#fdecef" },
	no_change: { label: "No Change", color: "#5f6478", bg: "#eef0f4" },
};

const COMPARE_META: Record<ComparedItem["status"], { label: string; color: string; bg: string }> = {
	new: { label: "New split", color: "#0d9488", bg: "#e0f4f1" },
	unchanged: { label: "In SF \u00b7 no change", color: "#12996a", bg: "#e4f6ee" },
	changed: { label: "In SF \u00b7 changed", color: "#c98a13", bg: "#fbf1de" },
	cancelled: { label: "Cancelled", color: "#dc3b52", bg: "#fdecef" },
};

const COMPARE_FIELD_LABELS: Record<string, string> = { rot: "Rot", length: "Len", startDate: "Start", endDate: "End", tollFreeNumber: "800#" };

// SF labels come from comparing splits against the linked deal; assumed labels are
// a subject-keyword guess shown until a deal is linked.
function LabelBadge({ email, padding }: { email: EmailRecord; padding: string }) {
	const m = KIND_META[email.label] || KIND_META.new;
	const assumed = email.labelSource !== "sf";
	return (
		<span title={assumed ? "Assumed from subject line \u2014 link a deal to compare against Salesforce" : "From Salesforce split comparison"} style={{ display: "inline-flex", alignItems: "center", padding, borderRadius: 5, fontSize: 10, fontWeight: 800, letterSpacing: ".2px", background: assumed ? "transparent" : m.bg, color: m.color, border: `1px ${assumed ? "dashed" : "solid"} ${assumed ? m.color : m.bg}`, fontFamily: "'Sora',sans-serif", textTransform: "uppercase", flex: "none" }}>
			{m.label}{assumed ? " ?" : ""}
		</span>
	);
}

// Amber outline on an input whose value differs from the matched SF split
const changedBorder = (item: ComparedItem | undefined, field: string) =>
	item?.changes?.some((c) => c.field === field) ? "1.5px solid #f59e0b" : "1px solid #dcdfea";

function CompareChip({ item }: { item?: ComparedItem }) {
	if (!item) return null;
	const m = COMPARE_META[item.status];
	return (
		<span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
			{item.changes?.map((c) => (
				<span key={c.field} style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: "#fef3c7", border: "1px solid #f5d27a", color: "#92400e", fontFamily: "ui-monospace,Menlo,monospace", whiteSpace: "nowrap" }}>
					{COMPARE_FIELD_LABELS[c.field] || c.field}: {String(c.sf ?? "-")} &rarr; {String(c.parsed ?? "-")}
				</span>
			))}
			<span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: m.bg, color: m.color, fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" }}>{m.label}</span>
		</span>
	);
}

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
	unprocessed: { label: "Unprocessed", color: "#4f46e5", bg: "#eeedfe" },
	processed: { label: "Processed", color: "#12996a", bg: "#e4f6ee" },
	error: { label: "Error", color: "#dc3b52", bg: "#fdecef" },
};


const FILTER_KEYS: [string, string][] = [
	["unprocessed", "Unprocessed"],
	["processed", "Processed"],
	["new", "New Traffic"],
	["revision", "Revision"],
	["cancellation", "Cancellation"],
	["no_change", "No Change"],
];

// ─── Helpers ──────────────────────────────────────────────────────────

function formatReceivedShort(dateStr: string): string {
	try {
		const d = new Date(dateStr);
		const now = new Date();
		const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
		if (d.toDateString() === now.toDateString()) return `Today ${time}`;
		const sameYear = d.getFullYear() === now.getFullYear();
		const date = sameYear
			? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
			: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
		return `${date}, ${time}`;
	} catch {
		return dateStr;
	}
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function mapApiEmailToRecord(e: any): EmailRecord {
	const pdfAtt = e.attachments?.find((a: any) => a.mimeType === "application/pdf" || a.filename?.toLowerCase().endsWith(".pdf"));
	const totalSize = e.attachments?.reduce((sum: number, a: any) => sum + (a.size || 0), 0) || 0;

	const hasParsedData = !!(e.groups || e.advertiser || e.campaign || e.confidence);
	let conf: EmailRecord["conf"] = "new";
	if (hasParsedData) conf = e.confidence === "failed" ? "error" : "ok";

	return {
		id: e.id,
		label: e.label || "new",
		labelSource: e.labelSource || "assumed",
		sfComparison: e.sfComparison || null,
		subject: e.subject || "(no subject)",
		advertiser: e.advertiser || "",
		brand: e.brand || "",
		agency: e.agency || "",
		from: e.from || e.fromRaw || "",
		size: formatSize(totalSize),
		network: e.network || "",
		campaign: e.campaign || "",
		flight: e.flightStart && e.flightEnd ? `${e.flightStart} \u2013 ${e.flightEnd}` : "",
		flightStart: e.flightStart || "",
		flightEnd: e.flightEnd || "",
		status: e.status || "unprocessed",
		unread: !hasParsedData,
		received: e.received || "",
		receivedShort: formatReceivedShort(e.received || ""),
		pdfName: pdfAtt?.filename || e.pdfFilename || "",
		pdfPages: 0,
		groups: e.groups || null,
		conf,
		attachments: e.attachments || [],
		body: e.body,
		dealMatches: e.dealMatches,
		selectedDealId: e.selectedDealId,
		trafficId: e.trafficId,
		trafficName: e.trafficName,
		pushCount: e.pushCount,
		pdfHash: e.pdfHash,
	};
}

const makeDraft = (e: EmailRecord): Draft => {
	const rots: Record<string, number> = {};
	const phones: Record<string, string> = {};
	const spotDates: Record<string, { start: string; end: string }> = {};
	(e.groups || []).forEach((g, gi) =>
		g.items.forEach((it, ii) => {
			const key = gi + ":" + ii;
			rots[key] = it.rot;
			phones[key] = it.tollFreeNumber || "";
			spotDates[key] = { start: it.startDate || "", end: it.endDate || "" };
		})
	);
	return {
		agency: e.agency,
		advertiser: e.advertiser,
		brand: e.brand,
		campaign: e.campaign,
		network: e.network,
		flightStart: e.flightStart,
		flightEnd: e.flightEnd,
		rots,
		phones,
		spotDates,
	};
};

// ─── Deal Section (collapsible, with search) ─────────────────────────

const SfCloudIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = "#9aa0b4" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M17.5 19.5H7C4.24 19.5 2 17.26 2 14.5C2 12.16 3.64 10.2 5.88 9.64C6.4 7 8.74 5 11.5 5C13.82 5 15.82 6.42 16.72 8.44C19.36 8.72 21.5 10.92 21.5 13.75C21.5 16.65 19.4 19.5 17.5 19.5Z" fill={color} opacity="0.8"/>
	</svg>
);

const DealCard: React.FC<{ deal: DealMatch; selected?: boolean; onClick?: () => void; sfInstanceUrl?: string }> = ({ deal, selected, onClick, sfInstanceUrl }) => (
	<div
		onClick={onClick}
		style={{
			display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", marginBottom: 6,
			borderRadius: 10, cursor: onClick ? "pointer" : "default",
			border: `1.5px solid ${selected ? "#4f46e5" : "#e9ebf1"}`,
			background: selected ? "#f5f5fe" : "#fff",
		}}
	>
		{onClick && (
			<span style={{
				width: 18, height: 18, borderRadius: "50%", border: `2px solid ${selected ? "#4f46e5" : "#d0d3df"}`,
				background: selected ? "#4f46e5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center",
				flex: "none", fontSize: 10, color: "#fff", fontWeight: 800,
			}}>
				{selected ? "\u2713" : ""}
			</span>
		)}
		<div style={{ flex: 1, minWidth: 0 }}>
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
				<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1e2e" }}>{deal.advertiser || deal.brand || deal.dealName}</span>
				{deal.brand && deal.advertiser && <span style={{ fontSize: 12, color: "#5f6478" }}>&middot; {deal.brand}</span>}
				<span style={{ marginLeft: "auto", fontSize: 11, color: "#9aa0b4" }}>{deal.status}</span>
			</div>
			<div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px", fontSize: 11, color: "#8a8fa3" }}>
				<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10, color: "#b7bcca" }}>{deal.dealId}</span>
				{deal.agency && <span>Agency: <strong style={{ color: "#5f6478" }}>{deal.agency}</strong></span>}
				{deal.program && <span>Program: <strong style={{ color: "#5f6478" }}>{deal.program}</strong></span>}
				{deal.campaign && <span>Campaign: <strong style={{ color: "#5f6478" }}>{deal.campaign}</strong></span>}
				{deal.quarter && <span>{deal.quarter} {deal.year}</span>}
				{deal.length && <span>:{deal.length}</span>}
			</div>
		</div>
		{sfInstanceUrl && (
			<a
				href={`${sfInstanceUrl}/${deal.dealId}`}
				target="_blank"
				rel="noopener noreferrer"
				onClick={(e) => e.stopPropagation()}
				title="View deal in Salesforce"
				style={{
					flex: "none", display: "flex", alignItems: "center", gap: 4,
					padding: "4px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
					color: "#0070d2", background: "#f0f7ff", border: "1px solid #d8eafd",
					textDecoration: "none", whiteSpace: "nowrap",
				}}
			>
				<SfCloudIcon size={13} color="#0070d2" />
				View
			</a>
		)}
	</div>
);

const DealSection: React.FC<{
	sel: EmailRecord;
	dealSearchQuery: string;
	dealSearchResults: DealMatch[];
	dealSearching: boolean;
	handleDealSearch: (q: string) => void;
	selectDeal: (deal: DealMatch) => void;
	sfInstanceUrl?: string;
}> = ({ sel, dealSearchQuery, dealSearchResults, dealSearching, handleDealSearch, selectDeal, sfInstanceUrl }) => {
	const filteredMatches = sel.dealMatches || [];
	const filteredSearch = dealSearchResults;
	const allDeals = [...filteredMatches, ...filteredSearch];
	const selectedDeal = sel.selectedDealId ? allDeals.find(d => d.dealId === sel.selectedDealId) : null;
	const hasDeal = !!selectedDeal;
	const [open, setOpen] = useState(!hasDeal);

	// Auto-collapse when a deal gets selected
	React.useEffect(() => {
		if (hasDeal) setOpen(false);
	}, [sel.selectedDealId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Auto-open when selection changes and no deal linked
	React.useEffect(() => {
		if (!sel.selectedDealId) setOpen(true);
	}, [sel.id]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div style={{ marginBottom: 20 }}>
			{/* Header */}
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: open || (hasDeal && !open) ? 8 : 0 }}>
				<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase" }}>
					Salesforce deal
				</span>
				{hasDeal ? (
					<span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 800, background: "#e4f6ee", color: "#12996a", fontFamily: "'Sora',sans-serif" }}>
						{"\u2713"} Linked
					</span>
				) : (
					<span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 800, background: "#fbf1de", color: "#92700e", fontFamily: "'Sora',sans-serif" }}>
						Not linked
					</span>
				)}
				<button
					onClick={() => setOpen(!open)}
					style={{ marginLeft: "auto", border: "1px solid #dcdfea", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#5f6478", cursor: "pointer", fontFamily: "'Sora',sans-serif" }}
				>
					{open ? "Collapse" : hasDeal ? "Change" : "Search"}
				</button>
			</div>

			{/* Collapsed: show confirmed deal */}
			{hasDeal && !open && <DealCard deal={selectedDeal!} selected sfInstanceUrl={sfInstanceUrl} />}

			{/* Expanded: search + results */}
			{open && (
				<div>
					{/* Search input */}
					<div style={{ position: "relative", marginBottom: 10 }}>
						<input
							value={dealSearchQuery}
							onChange={(e) => handleDealSearch(e.target.value)}
							placeholder="Search by Deal ID, advertiser, brand, campaign, or name..."
							style={{ width: "100%", padding: "9px 12px", border: "1px solid #dcdfea", borderRadius: 9, fontSize: 13, outline: "none", color: "#232838" }}
						/>
						{dealSearching && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9aa0b4" }}>Searching...</span>}
					</div>

					{/* Search results */}
					{filteredSearch.length > 0 && (
						<>
							<div style={{ fontSize: 10, fontWeight: 700, color: "#9aa0b4", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 6, fontFamily: "'Sora',sans-serif" }}>
								Search results ({filteredSearch.length})
							</div>
							<div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 10 }}>
								{filteredSearch.map((deal) => (
									<DealCard key={deal.dealId} deal={deal} selected={sel.selectedDealId === deal.dealId} onClick={() => selectDeal(deal)} sfInstanceUrl={sfInstanceUrl} />
								))}
							</div>
						</>
					)}
					{dealSearchQuery.length >= 2 && !dealSearching && filteredSearch.length === 0 && (
						<div style={{ padding: "12px", fontSize: 12.5, color: "#9aa0b4", textAlign: "center", marginBottom: 10 }}>No deals found for &quot;{dealSearchQuery}&quot;</div>
					)}

					{/* Auto-matched suggestions (only when not searching) */}
					{filteredMatches.length > 0 && !dealSearchQuery && (
						<>
							<div style={{ fontSize: 10, fontWeight: 700, color: "#9aa0b4", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 6, fontFamily: "'Sora',sans-serif" }}>
								Auto-matched suggestions ({filteredMatches.length})
							</div>
							<div style={{ maxHeight: 260, overflowY: "auto" }}>
								{filteredMatches.map((deal) => (
									<DealCard key={deal.dealId} deal={deal} selected={sel.selectedDealId === deal.dealId} onClick={() => selectDeal(deal)} sfInstanceUrl={sfInstanceUrl} />
								))}
							</div>
						</>
					)}

					{/* No suggestions, no search */}
					{filteredMatches.length === 0 && !dealSearchQuery && (
						<div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 14px", borderRadius: 10, background: "#fbf1de", border: "1px solid #e8d5a8" }}>
							<span style={{ fontSize: 14 }}>{"\u26a0\ufe0f"}</span>
							<span style={{ fontSize: 12.5, fontWeight: 600, color: "#92700e", lineHeight: 1.4 }}>No auto-matched deals. Use the search above to find the correct deal.</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

// ─── Component ────────────────────────────────────────────────────────

// ─── Activity Log Tab ─────────────────────────────────────────────────

interface LogEntry {
	id: number;
	loggedAt: string;
	actionType: string;
	category: string;
	description: string;
	emailId?: string;
	emailSubject?: string;
	dealId?: string;
	trafficId?: string;
	success?: boolean;
	details?: Record<string, any>;
	userName?: string;
}

const ACTION_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
	email_viewed: { icon: "\u2709", color: "#4f46e5", bg: "#eeedfe" },
	email_parsed: { icon: "\u2699", color: "#0d9488", bg: "#e0f4f1" },
	email_parse_failed: { icon: "\u26a0", color: "#dc3b52", bg: "#fdecef" },
	deal_selected: { icon: "\u2192", color: "#4f46e5", bg: "#eeedfe" },
	traffic_created: { icon: "\u2713", color: "#12996a", bg: "#e4f6ee" },
	traffic_create_failed: { icon: "\u2717", color: "#dc3b52", bg: "#fdecef" },
	splits_pushed: { icon: "\u2191", color: "#12996a", bg: "#e4f6ee" },
	splits_push_failed: { icon: "\u2717", color: "#dc3b52", bg: "#fdecef" },
	email_marked_done: { icon: "\u2713", color: "#12996a", bg: "#e4f6ee" },
	email_unmarked_done: { icon: "\u21ba", color: "#c98a13", bg: "#fbf1de" },
	field_edited: { icon: "\u270e", color: "#5f6478", bg: "#f2f3f7" },
};

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
	navigation: { label: "Navigation", color: "#4f46e5" },
	edit: { label: "Edit", color: "#c98a13" },
	salesforce: { label: "Salesforce", color: "#0d9488" },
	system: { label: "System", color: "#5f6478" },
};

const LogEntryRow: React.FC<{ entry: LogEntry; formatTime: (ts: string) => string }> = ({ entry, formatTime }) => {
	const [open, setOpen] = useState(false);
	const meta = ACTION_ICONS[entry.actionType] || { icon: "\u2022", color: "#5f6478", bg: "#f2f3f7" };
	const catMeta = CATEGORY_LABELS[entry.category] || { label: entry.category, color: "#5f6478" };
	const hasDetails = entry.details && Object.keys(entry.details).length > 0;
	const hasExtra = hasDetails || entry.emailId || entry.userName;

	return (
		<div style={{ marginBottom: 6, borderRadius: 11, background: "#fff", border: `1px solid ${open ? "#d7d5fb" : "#e9ebf1"}`, overflow: "hidden" }}>
			<div
				onClick={() => hasExtra && setOpen(!open)}
				style={{ display: "flex", gap: 12, padding: "12px 16px", alignItems: "flex-start", cursor: hasExtra ? "pointer" : "default" }}
			>
				{/* Icon */}
				<span style={{
					width: 30, height: 30, borderRadius: 8, background: meta.bg, color: meta.color,
					display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, flex: "none", marginTop: 2,
				}}>{meta.icon}</span>

				{/* Content */}
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
						<span style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1e2e", lineHeight: 1.3 }}>
							{entry.description}
						</span>
						{hasExtra && (
							<span style={{ marginLeft: "auto", fontSize: 11, color: "#9aa0b4", flex: "none" }}>
								{open ? "\u25b4" : "\u25be"}
							</span>
						)}
					</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", fontSize: 11.5, color: "#8a8fa3" }}>
						<span style={{ fontWeight: 700, color: catMeta.color }}>{catMeta.label}</span>
						<span>{formatTime(entry.loggedAt)}</span>
						{entry.dealId && <span>Deal: <strong style={{ color: "#5f6478" }}>{entry.dealId}</strong></span>}
						{entry.trafficId && <span>Traffic: <strong style={{ color: "#5f6478" }}>{entry.trafficId}</strong></span>}
						{entry.success === true && <span style={{ color: "#12996a", fontWeight: 700 }}>Success</span>}
						{entry.success === false && <span style={{ color: "#dc3b52", fontWeight: 700 }}>Failed</span>}
					</div>
					{entry.emailSubject && (
						<div style={{ fontSize: 11, color: "#b7bcca", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{entry.emailSubject}
						</div>
					)}
				</div>
			</div>

			{/* Expanded details */}
			{open && (
				<div style={{ padding: "0 16px 14px 58px", fontSize: 12, color: "#5f6478" }}>
					{entry.emailId && (
						<div style={{ display: "flex", gap: 8, padding: "4px 0" }}>
							<span style={{ color: "#9aa0b4", width: 90, flex: "none", fontWeight: 600 }}>Email ID</span>
							<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, color: "#333a52" }}>{entry.emailId}</span>
						</div>
					)}
					{entry.userName && (
						<div style={{ display: "flex", gap: 8, padding: "4px 0" }}>
							<span style={{ color: "#9aa0b4", width: 90, flex: "none", fontWeight: 600 }}>User</span>
							<span style={{ color: "#333a52" }}>{entry.userName}</span>
						</div>
					)}
					{hasDetails && Object.entries(entry.details!).map(([k, v]) => (
						<div key={k} style={{ display: "flex", gap: 8, padding: "4px 0" }}>
							<span style={{ color: "#9aa0b4", width: 90, flex: "none", fontWeight: 600 }}>{k}</span>
							<span style={{ color: "#333a52", wordBreak: "break-word" }}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const ActivityLog: React.FC = () => {
	const [entries, setEntries] = useState<LogEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [catFilter, setCatFilter] = useState("all");
	const [sfOnly, setSfOnly] = useState(false);
	const [logSearch, setLogSearch] = useState("");

	const fetchLog = useCallback(async () => {
		setLoading(true);
		try {
			const params: Record<string, string> = { limit: "500" };
			if (catFilter !== "all") params.category = catFilter;
			const res = await authAxios({ method: "GET", url: `${API}/salesforce/traffic-instructions/action-log`, params });
			console.log("[ActivityLog] fetched", res.data?.count, "entries");
			setEntries(res.data.entries || []);
		} catch (err) { console.error("[ActivityLog] fetch failed:", err); setEntries([]); }
		finally { setLoading(false); }
	}, [catFilter]);

	useEffect(() => { fetchLog(); }, [fetchLog]);

	const cats = ["all", "navigation", "salesforce", "system"];
	const filtered = useMemo(() => {
		let list = sfOnly ? entries.filter(e => e.category === "salesforce") : entries;
		if (logSearch.trim()) {
			const q = logSearch.toLowerCase();
			list = list.filter(e =>
				e.description?.toLowerCase().includes(q) ||
				e.emailSubject?.toLowerCase().includes(q) ||
				e.dealId?.toLowerCase().includes(q) ||
				e.trafficId?.toLowerCase().includes(q) ||
				e.actionType?.toLowerCase().includes(q) ||
				e.userName?.toLowerCase().includes(q) ||
				(e.details && JSON.stringify(e.details).toLowerCase().includes(q))
			);
		}
		return list;
	}, [entries, sfOnly, logSearch]);

	const formatTime = (ts: string) => {
		try {
			const d = new Date(ts);
			return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
		} catch { return ts; }
	};

	return (
		<div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, background: "#f3f4f8" }}>
			{/* Toolbar */}
			<div style={{ flex: "none", display: "flex", alignItems: "center", gap: 10, padding: "14px 24px", background: "#fff", borderBottom: "1px solid #e6e7ef" }}>
				<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase" }}>Activity log</span>
				<div style={{ display: "flex", gap: 6, marginLeft: 12 }}>
					{cats.map(c => {
						const active = catFilter === c;
						const label = c === "all" ? "All" : (CATEGORY_LABELS[c]?.label || c);
						return (
							<button key={c} onClick={() => setCatFilter(c)} style={{
								padding: "5px 11px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
								border: `1px solid ${active ? "#4f46e5" : "#e4e6ef"}`, background: active ? "#4f46e5" : "#f2f3f7",
								color: active ? "#fff" : "#5f6478", fontFamily: "'Sora',sans-serif",
							}}>{label}</button>
						);
					})}
				</div>
				<label style={{ marginLeft: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#5f6478", fontWeight: 600, cursor: "pointer" }}>
					<input type="checkbox" checked={sfOnly} onChange={() => setSfOnly(!sfOnly)} />
					Salesforce only
				</label>
				<div style={{ position: "relative", marginLeft: 12 }}>
					<span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9aa0b4", fontSize: 13 }}>{"\u2315"}</span>
					<input
						value={logSearch}
						onChange={(e) => setLogSearch(e.target.value)}
						placeholder="Search deal, agency, keyword..."
						style={{ width: 220, padding: "7px 10px 7px 28px", border: "1px solid #dcdfea", borderRadius: 8, fontSize: 12.5, outline: "none", color: "#232838", background: "#f8f9fc" }}
					/>
					{logSearch && (
						<button onClick={() => setLogSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", color: "#9aa0b4", fontSize: 14, cursor: "pointer", padding: 0, lineHeight: 1 }}>&times;</button>
					)}
				</div>
				<button onClick={fetchLog} disabled={loading} style={{
					marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, background: loading ? "#9aa0b4" : "#4f46e5",
					border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#fff", padding: "9px 15px",
					cursor: loading ? "default" : "pointer", fontFamily: "'Sora',sans-serif",
				}}>&#8635; {loading ? "Loading..." : "Refresh"}</button>
			</div>

			{/* Entries */}
			<div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "16px 24px" }}>
				<div style={{ maxWidth: 900, margin: "0 auto" }}>
					{loading && entries.length === 0 && (
						<div style={{ textAlign: "center", padding: 40, color: "#9aa0b4", fontSize: 14 }}>Loading activity log...</div>
					)}
					{!loading && entries.length === 0 && (
						<div style={{ textAlign: "center", padding: 40, color: "#9aa0b4", fontSize: 14 }}>No activity logged yet. Actions will appear here as you use Traffic Instructions.</div>
					)}
					{!loading && entries.length > 0 && filtered.length === 0 && logSearch && (
						<div style={{ textAlign: "center", padding: 40, color: "#9aa0b4", fontSize: 14 }}>No results for &quot;{logSearch}&quot;</div>
					)}
					{filtered.length > 0 && logSearch && (
						<div style={{ fontSize: 12, color: "#9aa0b4", marginBottom: 10 }}>{filtered.length} result{filtered.length !== 1 ? "s" : ""} for &quot;{logSearch}&quot;</div>
					)}
					{filtered.map((entry) => <LogEntryRow key={entry.id} entry={entry} formatTime={formatTime} />)}
				</div>
			</div>
		</div>
	);
};

// ─── Main Component ───────────────────────────────────────────────────

const TrafficInstructions: React.FC = () => {
	const [topView, setTopView] = useState<"inbox" | "log">("inbox");
	const [emails, setEmails] = useState<EmailRecord[]>([]);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [datePreset, setDatePreset] = useState(0); // default to "Recent"
	const [activeFilter, setActiveFilter] = useState("unprocessed");
	const [search, setSearch] = useState("");
	const [drafts, setDrafts] = useState<Record<string, Draft>>({});
	const [modalOpen, setModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<"details" | "lineItems">("details");
	const [phase, setPhase] = useState<"confirm" | "pushing" | "done">("confirm");
	const [sourceOpen, setSourceOpen] = useState(true);
	const [inboxOpen, setInboxOpen] = useState(true);
	// Layout presets: default (all panels), emailCollapsed, pdfCollapsed, workOnly
	const [layoutMode, setLayoutMode] = useState<"default" | "emailCollapsed" | "pdfCollapsed" | "workOnly">("default");
	const [loading, setLoading] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [parsing, setParsing] = useState(false);
	const [pushError, setPushError] = useState<string | null>(null);
	const [pushResult, setPushResult] = useState<{ splitsCreated: number; splitsEnded?: number; splitsDeleted?: number } | null>(null);
	const [lastSynced, setLastSynced] = useState<Date | null>(null);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [pdfFullscreen, setPdfFullscreen] = useState(false);
	const [dealSearchQuery, setDealSearchQuery] = useState("");
	const [dealSearchResults, setDealSearchResults] = useState<DealMatch[]>([]);
	const [dealSearching, setDealSearching] = useState(false);
	// Track which line-item groups are excluded from the push, keyed by email ID → set of stable group keys (daypart|length).
	const [excludedGroups, setExcludedGroups] = useState<Record<string, Set<string>>>({});
	// Individual splits excluded from the push, keyed by email ID → set of "groupIndex:itemIndex".
	const [excludedItems, setExcludedItems] = useState<Record<string, Set<string>>>({});
	const dealSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevPdfRef = useRef<string | null>(null);
	const [sfInstanceUrl, setSfInstanceUrl] = useState("");

	// Sync layout mode to panel open states
	useEffect(() => {
		switch (layoutMode) {
			case "default": setInboxOpen(true); setSourceOpen(true); break;
			case "emailCollapsed": setInboxOpen(false); setSourceOpen(true); break;
			case "pdfCollapsed": setInboxOpen(true); setSourceOpen(false); break;
			case "workOnly": setInboxOpen(false); setSourceOpen(false); break;
		}
	}, [layoutMode]);

	// Helper: toggle a panel and update layoutMode to match
	const togglePanel = useCallback((panel: "inbox" | "source", open: boolean) => {
		if (panel === "inbox") {
			setLayoutMode(open ? (sourceOpen ? "default" : "pdfCollapsed") : (sourceOpen ? "emailCollapsed" : "workOnly"));
		} else {
			setLayoutMode(open ? (inboxOpen ? "default" : "emailCollapsed") : (inboxOpen ? "pdfCollapsed" : "workOnly"));
		}
	}, [inboxOpen, sourceOpen]);

	// ── Fetch emails from API ──
	// Parse a single email by ID against current state, returns updated EmailRecord or null
	const parseOne = useCallback(async (email: EmailRecord): Promise<EmailRecord | null> => {
		try {
			const pdfAtt = email.attachments.find((a) => a.mimeType === "application/pdf" || a.filename?.toLowerCase().endsWith(".pdf"));
			const res = await authAxios({
				method: "POST",
				url: `${API}/salesforce/traffic-instructions/${email.id}/parse`,
				data: { attachmentId: pdfAtt?.attachmentId },
			});
			const parsed = res.data.parsed;
			const dealMatches: DealMatch[] = res.data.dealMatches || [];
			const selectedDealId: string | null = res.data.selectedDealId || null;
			const groups: DaypartGroup[] = parsed.groups || [];
			const pdfHash: string = res.data.pdfHash || "";
			let conf: EmailRecord["conf"] = "ok";
			if (parsed.confidence === "failed") conf = "error";

			return {
				...email,
				conf,
				unread: false,
				advertiser: parsed.advertiser || email.advertiser,
				brand: parsed.brand || email.brand,
				agency: parsed.agency || email.agency,
				campaign: parsed.campaign || email.campaign,
				network: parsed.network || email.network,
				flightStart: parsed.flightStart || email.flightStart,
				flightEnd: parsed.flightEnd || email.flightEnd,
				flight: parsed.flightStart && parsed.flightEnd ? `${parsed.flightStart} \u2013 ${parsed.flightEnd}` : email.flight,
				groups,
				dealMatches,
				selectedDealId,
				pdfHash,
				label: res.data.label || email.label,
				labelSource: res.data.labelSource || "assumed",
				sfComparison: null,
			};
		} catch {
			return { ...email, conf: "error" };
		}
	}, []);

	// ── Background preprocessing queue ──
	const parseQueueRef = useRef<string[]>([]);
	const parseActiveRef = useRef(false);
	const syncGenRef = useRef(0);

	const processParseQueue = useCallback(async () => {
		if (parseActiveRef.current) return;
		parseActiveRef.current = true;
		const gen = syncGenRef.current;
		while (parseQueueRef.current.length > 0) {
			if (gen !== syncGenRef.current) break;
			const id = parseQueueRef.current[0];
			parseQueueRef.current = parseQueueRef.current.slice(1);
			if (parsedIdsRef.current.has(id)) continue;
			parsedIdsRef.current.add(id);
			const email = emailsRef.current.find(e => e.id === id);
			if (!email || email.conf !== "new") continue;
			try {
				const updated = await parseOne(email);
				if (updated) {
					setEmails(prev => prev.map(e => e.id === updated.id ? updated : e));
					setDrafts(prev => ({ ...prev, [updated.id]: makeDraft(updated) }));
				}
			} catch { /* continue to next */ }
		}
		parseActiveRef.current = false;
	}, [parseOne]);

	// Load emails from DynamoDB cache (fast, no Gmail)
	const loadEmails = useCallback(async () => {
		try {
			const res = await authAxios({ method: "GET", url: `${API}/salesforce/traffic-instructions` });
			const fetched: EmailRecord[] = (res.data.emails || []).map(mapApiEmailToRecord);
			fetched.forEach(e => { if (e.conf !== "new") parsedIdsRef.current.add(e.id); });
			setEmails(fetched);
			// Functional update: the mount-time sync calls this from a stale closure where selectedId is still null
			if (fetched.length > 0) setSelectedId((prev) => prev ?? fetched[0].id);
			if (res.data.sfInstanceUrl) setSfInstanceUrl(res.data.sfInstanceUrl);
			setLastSynced(new Date());
		} catch (err) {
			console.error("Failed to load emails:", err);
		}
	}, []);

	// Full sync: load cache first (instant), then sync Gmail in background and reload
	const syncEmails = useCallback(async (forceSync = false) => {
		// Load cached emails immediately so the UI renders right away
		await loadEmails();
		setLoading(false);

		if (!forceSync) return;

		// Background sync: fetch Gmail + parse, then reload with any new data
		setSyncing(true);
		syncGenRef.current++;
		try {
			try {
				await authAxios({ method: "POST", url: `${API}/salesforce/traffic-instructions/sync-all`, timeout: 120000 });
			} catch (syncErr) {
				console.warn("Sync-all failed:", syncErr);
			}
			// Reload from cache to pick up any new emails from sync
			await loadEmails();
		} catch (err) {
			console.error("Failed to sync traffic emails:", err);
		} finally {
			setSyncing(false);
			setLoading(false);
		}
	}, [selectedId, datePreset, loadEmails]);

	// Auto-sync on mount (Gmail fetch + parse + load)
	useEffect(() => {
		syncEmails(true);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Start background preprocessing after sync completes
	useEffect(() => {
		if (!syncing && emails.length > 0 && !parseActiveRef.current) {
			const unparsed = emails.filter(e => e.conf === "new" && !parsedIdsRef.current.has(e.id));
			if (unparsed.length > 0) {
				parseQueueRef.current = unparsed.map(e => e.id);
				processParseQueue();
			}
		}
	}, [syncing]); // eslint-disable-line react-hooks/exhaustive-deps

	// Load PDF preview and email body when selection changes
	const prevSelectedRef = useRef<string | null>(null);
	useEffect(() => {
		if (!selectedId || selectedId === prevSelectedRef.current) return;
		prevSelectedRef.current = selectedId;

		// Revoke old blob URL
		if (prevPdfRef.current) {
			URL.revokeObjectURL(prevPdfRef.current);
			prevPdfRef.current = null;
		}
		setPdfUrl(null);

		const sel = emails.find((e) => e.id === selectedId);
		if (!sel) return;

		// Fetch email body for source panel (if not already loaded)
		if (!sel.body) {
			authAxios({
				method: "GET",
				url: `${API}/salesforce/traffic-instructions/${sel.id}`,
			}).then((res) => {
				if (res.data.body) {
					setEmails((prev) => prev.map((e) => e.id === sel.id ? { ...e, body: res.data.body } : e));
				}
			}).catch(() => { /* body fetch failed */ });
		}

		// Fetch PDF - try S3 first (faster), falls back to Gmail internally
		authAxios({
			method: "GET",
			url: `${API}/salesforce/traffic-instructions/${sel.id}/pdf`,
			responseType: "blob",
		}).then((res) => {
			const url = URL.createObjectURL(res.data);
			setPdfUrl(url);
			prevPdfRef.current = url;
		}).catch(() => { /* PDF preview not available */ });
	}, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Auto-parse selected email if it hasn't been parsed yet.
	// Only depends on selectedId — reads emails from ref to avoid
	// re-triggering when emails state updates (e.g. after parse completes).
	const parsedIdsRef = useRef<Set<string>>(new Set());
	const emailsRef = useRef(emails);
	emailsRef.current = emails;
	useEffect(() => {
		if (!selectedId) return;
		const email = emailsRef.current.find((e) => e.id === selectedId);
		if (!email || email.conf !== "new" || parsedIdsRef.current.has(selectedId)) return;
		parsedIdsRef.current.add(selectedId);
		setParsing(true);
		parseOne(email).then((updated) => {
			if (updated) {
				setEmails((prev) => prev.map((e) => e.id === updated.id ? updated : e));
				setDrafts((prev) => ({ ...prev, [updated.id]: makeDraft(updated) }));
				// Auto-retry with OCR if first parse returned error (scanned PDF)
				if (updated.conf === "error") {
					parseOne(updated).then((retried) => {
						if (retried) {
							setEmails((prev) => prev.map((e) => e.id === retried.id ? retried : e));
							setDrafts((prev) => ({ ...prev, [retried.id]: makeDraft(retried) }));
						}
					}).catch(() => { /* retry failed */ }).finally(() => setParsing(false));
					return;
				}
			}
		}).catch(() => { /* parse failed */ }).finally(() => setParsing(false));
	}, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

	const sel = useMemo(() => emails.find((e) => e.id === selectedId) || null, [emails, selectedId]);

	// Rebuild draft when parsed data changes (e.g. after re-parse fills in dates/phones)
	const selGroupsKey = sel?.groups ? JSON.stringify(sel.groups.map(g => g.items.length)) : "";
	useEffect(() => {
		if (selectedId && sel?.groups && sel.groups.length > 0) {
			const existing = drafts[selectedId];
			if (existing) {
				// Check if any spot dates/phones are in sel but missing in draft
				const fresh = makeDraft(sel);
				const hasMissingData = Object.entries(fresh.spotDates).some(
					([k, v]) => (v.start || v.end) && (!existing.spotDates[k]?.start && !existing.spotDates[k]?.end)
				) || Object.entries(fresh.phones).some(
					([k, v]) => v && !existing.phones[k]
				);
				if (hasMissingData) {
					setDrafts((prev) => ({ ...prev, [selectedId]: fresh }));
				}
			}
		}
	}, [selectedId, selGroupsKey]); // eslint-disable-line react-hooks/exhaustive-deps

	const draft = useMemo(() => (selectedId && drafts[selectedId]) || (sel ? makeDraft(sel) : null), [drafts, selectedId, sel]);

	const select = useCallback(
		(id: string) => {
			if (id === selectedId) return; // already selected — don't reset state
			setSelectedId(id);
			const e = emails.find((em) => em.id === id);
			if (e) {
				logTiAction({
					actionType: "email_viewed", category: "navigation",
					description: `Opened email: ${e.subject}`,
					emailId: id, emailSubject: e.subject,
				});
			}
			// Reset all per-email state when switching emails
			setDealSearchQuery("");
			setDealSearchResults([]);
			setTrafficError(null);
			setPhase("confirm");
			setModalOpen(false);
			setPushError(null);
			setPdfUrl(null);
			setActiveTab("details");
			setDrafts((prev) => {
				if (prev[id]) return prev;
				return { ...prev, [id]: makeDraft(e!) };
			});
		},
		[emails, selectedId]
	);

	const setField = useCallback(
		(k: keyof Draft, v: string) => {
			if (!selectedId) return;
			setDrafts((prev) => ({
				...prev,
				[selectedId]: { ...(prev[selectedId] || makeDraft(sel!)), [k]: v },
			}));
		},
		[selectedId, sel]
	);

	const setRot = useCallback(
		(key: string, v: string) => {
			if (!selectedId) return;
			setDrafts((prev) => ({
				...prev,
				[selectedId]: {
					...(prev[selectedId] || makeDraft(sel!)),
					rots: { ...(prev[selectedId] || makeDraft(sel!)).rots, [key]: Number(v) || 0 },
				},
			}));
		},
		[selectedId, sel]
	);

	const setPhone = useCallback(
		(key: string, v: string) => {
			if (!selectedId) return;
			setDrafts((prev) => {
				const d = prev[selectedId] || makeDraft(sel!);
				return { ...prev, [selectedId]: { ...d, phones: { ...d.phones, [key]: v } } };
			});
		},
		[selectedId, sel]
	);

	const setSpotDate = useCallback(
		(key: string, field: "start" | "end", v: string) => {
			if (!selectedId) return;
			setDrafts((prev) => {
				const d = prev[selectedId] || makeDraft(sel!);
				const cur = d.spotDates[key] || { start: "", end: "" };
				return { ...prev, [selectedId]: { ...d, spotDates: { ...d.spotDates, [key]: { ...cur, [field]: v } } } };
			});
		},
		[selectedId, sel]
	);

	const applyToAllSpots = useCallback(
		(field: "phone" | "startDate" | "endDate", value: string, groupIndex?: number) => {
			if (!selectedId || !sel?.groups) return;
			setDrafts((prev) => {
				const d = prev[selectedId] || makeDraft(sel!);
				const phones = { ...d.phones };
				const spotDates = { ...d.spotDates };
				sel.groups!.forEach((g, gi) => {
					if (groupIndex !== undefined && gi !== groupIndex) return;
					g.items.forEach((_, ii) => {
						const key = gi + ":" + ii;
						if (field === "phone") phones[key] = value;
						else {
							const cur = spotDates[key] || { start: "", end: "" };
							spotDates[key] = { ...cur, [field === "startDate" ? "start" : "end"]: value };
						}
					});
				});
				return { ...prev, [selectedId]: { ...d, phones, spotDates } };
			});
		},
		[selectedId, sel]
	);

	// ── Compare parsed line items with the linked deal's SF splits; derives the labels ──
	const compareWithSf = useCallback((emailId: string, dealId: string, groups: DaypartGroup[] | null, flightStart: string, flightEnd: string) => {
		authAxios({ method: "POST", url: `${API}/salesforce/traffic-instructions/${emailId}/compare`, data: { dealId, groups: groups || [], flightStart, flightEnd } })
			.then((res) => {
				setEmails((prev) => prev.map((e) =>
					e.id === emailId && e.selectedDealId === dealId ? { ...e, label: res.data.label || e.label, labelSource: res.data.labelSource || "assumed", sfComparison: res.data.sfComparison || null } : e
				));
			})
			.catch(() => { /* comparison failed — labels stay as they were */ });
	}, []);

	// ── Select a deal from the matches and apply its fields ──
	const selectDeal = useCallback(
		(deal: DealMatch) => {
			if (!selectedId || !sel) return;
			logTiAction({
				actionType: "deal_selected", category: "navigation",
				description: `Linked Deal ${deal.dealId} (${deal.advertiser || deal.dealName}) to email`,
				emailId: selectedId, emailSubject: sel.subject, dealId: deal.dealId,
				details: { advertiser: deal.advertiser, brand: deal.brand, campaign: deal.campaign, quarter: deal.quarter, year: deal.year },
			});
			setEmails((prev) =>
				prev.map((e) => (e.id === selectedId ? { ...e, selectedDealId: deal.dealId, trafficId: null, trafficName: null, labelSource: "assumed", sfComparison: null } : e))
			);
			authAxios({ method: "PATCH", url: `${API}/salesforce/traffic-instructions/${selectedId}/state`, data: { selectedDealId: deal.dealId, trafficId: null, trafficName: null } }).catch(() => { /* persist non-blocking */ });
			setDrafts((prev) => ({
				...prev,
				[selectedId]: {
					...(prev[selectedId] || makeDraft(sel)),
					advertiser: deal.advertiser || prev[selectedId]?.advertiser || "",
					agency: deal.agency || prev[selectedId]?.agency || "",
					brand: deal.brand || prev[selectedId]?.brand || "",
					campaign: deal.campaign || prev[selectedId]?.campaign || "",
					network: deal.program || prev[selectedId]?.network || "",
				},
			}));
			// Auto-exclude groups whose length doesn't match the deal's length
			const dl = deal.length ? parseInt(String(deal.length), 10) : null;
			if (dl && sel.groups) {
				const excluded = new Set<string>();
				sel.groups.forEach((g) => {
					const lengths = Array.from(new Set(g.items.map(it => it.length || 0)));
					lengths.forEach((len) => {
						if (len !== dl) excluded.add(`${g.daypart}|${len}`);
					});
				});
				setExcludedGroups((prev) => ({ ...prev, [selectedId]: excluded }));
			}
			// Look up Traffic__c record and existing splits for this deal
			authAxios({ method: "GET", url: `${API}/salesforce/traffic-instructions/deal/${deal.dealId}/traffic` })
				.then((res) => {
					const t = res.data.traffic;
					const sfSplits: any[] = res.data.splits || [];
					setEmails((prev) => prev.map((e) =>
						e.id === selectedId ? { ...e, trafficId: t?.Id || null, trafficName: t?.Name || null } : e
					));
					if (t?.Id) authAxios({ method: "PATCH", url: `${API}/salesforce/traffic-instructions/${selectedId}/state`, data: { trafficId: t.Id, trafficName: t.Name } }).catch(() => { /* persist non-blocking */ });

					// If existing splits found and no parsed groups yet, populate from SF.
					// Parsed groups are kept otherwise — they're what gets compared against SF.
					if (sfSplits.length > 0 && !sel.groups?.length) {
						const sfGroups: DaypartGroup[] = [];
						const groupMap = new Map<string, SpotItem[]>();
						for (const s of sfSplits) {
							const daypart = s.Days_Allowed__c || "ROS";
							const key = daypart;
							if (!groupMap.has(key)) groupMap.set(key, []);
							groupMap.get(key)!.push({
								isci: s.ISCI__c || "",
								title: s.Spot_Name__c || "",
								length: parseInt(s.Length__c) || 30,
								rot: s.Rotation__c || 0,
								tollFreeNumber: s.X800_Number__c || "",
								startDate: s.Start_Date__c || "",
								endDate: s.End_Date__c || "",
							});
						}
						Array.from(groupMap.entries()).forEach(([daypart, items]) => {
							const match = sfSplits.find(s => (s.Days_Allowed__c || "ROS") === daypart);
							const window = match?.Start_Time__c ? `${match.Start_Time__c}-${match.End_Time__c || ""}` : "";
							sfGroups.push({ daypart, window, items });
						});
						setEmails((prev) => prev.map((e) =>
							e.id === selectedId ? { ...e, groups: sfGroups, sfSplitsLoaded: true } : e
						));
						// Rebuild draft with the SF splits
						const updated = { ...sel!, groups: sfGroups };
						setDrafts((prev) => ({ ...prev, [selectedId]: makeDraft(updated as any) }));
					}
				})
				.catch(() => { /* traffic lookup failed — user can create manually */ });
		},
		[selectedId, sel]
	);

	// ── Run the SF comparison whenever the linked deal has none (deal just selected, or linked before labels came from SF) ──
	useEffect(() => {
		if (!sel?.selectedDealId || sel.sfComparison?.dealId === sel.selectedDealId) return;
		compareWithSf(sel.id, sel.selectedDealId, sel.groups, sel.flightStart, sel.flightEnd);
	}, [sel?.id, sel?.selectedDealId]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Auto-exclude mismatched lengths when deal loaded from cache ──
	useEffect(() => {
		if (!sel?.selectedDealId || !sel.groups || !selectedId) return;
		// Skip if user already has explicit exclusions for this email
		if (excludedGroups[selectedId]?.size) return;
		const allDeals = [...(sel.dealMatches || [])];
		const deal = allDeals.find((d) => d.dealId === sel.selectedDealId);
		const dl = deal?.length ? parseInt(String(deal.length), 10) : null;
		if (!dl) return;
		const excluded = new Set<string>();
		sel.groups.forEach((g) => {
			const lengths = Array.from(new Set(g.items.map((it) => it.length || 0)));
			lengths.forEach((len) => {
				if (len !== dl) excluded.add(`${g.daypart}|${len}`);
			});
		});
		if (excluded.size > 0) {
			setExcludedGroups((prev) => ({ ...prev, [selectedId]: excluded }));
		}
	}, [selectedId, sel?.selectedDealId]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Create Traffic__c for a deal ──
	const [creatingTraffic, setCreatingTraffic] = useState(false);
	const [trafficError, setTrafficError] = useState<string | null>(null);
	const createTraffic = useCallback(async () => {
		if (!sel?.selectedDealId || !draft) return;
		setCreatingTraffic(true);
		setTrafficError(null);
		try {
			const res = await authAxios({
				method: "POST",
				url: `${API}/salesforce/traffic-instructions/deal/${sel.selectedDealId}/traffic`,
				data: {
					campaign: draft.campaign,
					flightStart: draft.flightStart,
					flightEnd: draft.flightEnd,
					advertiser: draft.advertiser,
				},
			});
			setEmails((prev) => prev.map((e) =>
				e.id === sel.id ? { ...e, trafficId: res.data.trafficId, trafficName: res.data.trafficId } : e
			));
			authAxios({ method: "PATCH", url: `${API}/salesforce/traffic-instructions/${sel.id}/state`, data: { trafficId: res.data.trafficId, trafficName: res.data.trafficId } }).catch(() => { /* persist non-blocking */ });
		} catch (err: any) {
			const msg = err.response?.data?.error || err.message || "Failed to create traffic";
			console.error("Failed to create traffic:", msg);
			setTrafficError(msg);
		} finally {
			setCreatingTraffic(false);
		}
	}, [sel, draft]);

	// ── Manual deal search (debounced) ──
	const handleDealSearch = useCallback((q: string) => {
		setDealSearchQuery(q);
		if (dealSearchTimer.current) clearTimeout(dealSearchTimer.current);
		if (q.trim().length < 2) {
			setDealSearchResults([]);
			return;
		}
		setDealSearching(true);
		dealSearchTimer.current = setTimeout(async () => {
			try {
				const res = await authAxios({ method: "GET", url: `${API}/salesforce/traffic-instructions/deals/search`, params: { q } });
				setDealSearchResults(res.data.deals || []);
			} catch { setDealSearchResults([]); }
			finally { setDealSearching(false); }
		}, 250);
	}, []);

	// ── Parse ──
	const runParse = useCallback(async () => {
		if (!sel) return;
		setParsing(true);
		try {
			const parsePdfs = sel.attachments.filter((a) => a.mimeType === "application/pdf" || a.filename?.toLowerCase().endsWith(".pdf"));
			const pdfAtt = parsePdfs.find((a) => !/^coverpage\b/i.test(a.filename)) || parsePdfs[0];
			const res = await authAxios({
				method: "POST",
				url: `${API}/salesforce/traffic-instructions/${sel.id}/parse`,
				data: { attachmentId: pdfAtt?.attachmentId },
			});

			const parsed = res.data.parsed;
			const confidence = parsed.confidence;

			// Map confidence to conf
			let conf: EmailRecord["conf"] = "ok";
			if (confidence === "failed") conf = "error";
			else if (confidence === "low" || confidence === "medium") conf = "ok"; // still show parsed view

			const groups: DaypartGroup[] = parsed.groups || [];

			setEmails((prev) =>
				prev.map((e) =>
					e.id === sel.id
						? {
								...e,
								conf,
								unread: false,
								advertiser: parsed.advertiser || e.advertiser,
								brand: parsed.brand || e.brand,
								agency: parsed.agency || e.agency,
								campaign: parsed.campaign || e.campaign,
								network: parsed.network || e.network,
								flightStart: parsed.flightStart || e.flightStart,
								flightEnd: parsed.flightEnd || e.flightEnd,
								flight: parsed.flightStart && parsed.flightEnd ? `${parsed.flightStart} \u2013 ${parsed.flightEnd}` : e.flight,
								groups,
								label: res.data.label || e.label,
								labelSource: res.data.labelSource || "assumed",
								sfComparison: null,
							}
						: e
				)
			);
			if (sel.selectedDealId) {
				compareWithSf(sel.id, sel.selectedDealId, groups, parsed.flightStart || sel.flightStart, parsed.flightEnd || sel.flightEnd);
			}

			// Update draft with parsed values
			setDrafts((prev) => ({
				...prev,
				[sel.id]: makeDraft({
					...sel,
					advertiser: parsed.advertiser || sel.advertiser,
					brand: parsed.brand || sel.brand,
					agency: parsed.agency || sel.agency,
					campaign: parsed.campaign || sel.campaign,
					network: parsed.network || sel.network,
					flightStart: parsed.flightStart || sel.flightStart,
					flightEnd: parsed.flightEnd || sel.flightEnd,
					groups,
				}),
			}));
		logTiAction({
				actionType: "email_parsed", category: "system", success: true,
				description: `Parsed email: ${sel.subject} — ${groups.length} daypart(s), ${groups.reduce((n, g) => n + g.items.length, 0)} spot(s)`,
				emailId: sel.id, emailSubject: sel.subject,
				details: { advertiser: parsed.advertiser, campaign: parsed.campaign, network: parsed.network, confidence: parsed.confidence },
			});
		} catch (err) {
			console.error("Parse failed:", err);
			setEmails((prev) => prev.map((e) => (e.id === sel.id ? { ...e, conf: "error" } : e)));
			logTiAction({
				actionType: "email_parse_failed", category: "system", success: false,
				description: `Parse failed for email: ${sel.subject}`,
				emailId: sel.id, emailSubject: sel.subject,
			});
		} finally {
			setParsing(false);
		}
	}, [sel, compareWithSf]);

	// ── Push to Salesforce ──
	const onConfirmPush = useCallback(async () => {
		if (phase === "done") {
			setModalOpen(false);
			return;
		}
		if (!sel || !draft) return;

		setPhase("pushing");
		setPushError(null);

		try {
			const { data: result } = await authAxios({
				method: "POST",
				url: `${API}/salesforce/traffic-instructions/${sel.id}/push`,
				data: {
					kind: sel.label === "no_change" ? "revision" : sel.label,
					trafficId: sel.trafficId,
					parsed: {
						agency: draft.agency,
						advertiser: draft.advertiser,
						brand: draft.brand,
						campaign: draft.campaign,
						network: draft.network,
						flightStart: draft.flightStart,
						flightEnd: draft.flightEnd,
						// Only include groups not excluded by the user
						groups: groups
							.filter(g => !curExcluded.has(g.groupKey))
							.map(g => ({ daypart: g.daypart, window: g.window, items: g.items.filter(it => it.included).map(it => ({
								isci: it.isci, title: it.title, length: it.length, rot: it.rot,
								tollFreeNumber: it.phone || "", startDate: it.spotStart || "", endDate: it.spotEnd || "",
							})) }))
							.filter(g => g.items.length > 0),
						confidence: "high",
						raw: "",
					},
				},
			});

			// Track push count — don't auto-mark as processed since email
			// may have multiple deals to push to (e.g. :15 and :30).
			setEmails((prev) =>
				prev.map((e) => e.id === sel.id ? { ...e, pushCount: (e.pushCount || 0) + 1 } : e)
			);
			setPushResult(result);
			setPhase("done");
		} catch (err: any) {
			console.error("Push failed:", err);
			// SF changed since the comparison the user saw: show the fresh one
			if (err.response?.status === 409 && err.response.data?.sfComparison) {
				const d = err.response.data;
				setEmails((prev) => prev.map((e) => e.id === sel.id ? { ...e, label: d.label, labelSource: d.labelSource, sfComparison: d.sfComparison } : e));
			}
			setPushError(err.response?.data?.message || err.message || "Push failed");
			setPhase("confirm");
		}
	}, [sel, draft, phase]);

	// Filter emails to selected date window so counts match visible list
	const dateWindowEmails = useMemo(() => {
		const preset = DATE_PRESETS[datePreset];
		if (!preset) return emails;
		const afterMatch = preset.q.match(/after:(\d{4}\/\d{2}\/\d{2})/);
		if (!afterMatch) return emails;
		const cutoff = new Date(afterMatch[1].replace(/\//g, "-")).getTime();
		return emails.filter((e) => new Date(e.received).getTime() >= cutoff);
	}, [emails, datePreset]);

	// ── Derived values ──
	if (loading) {
		return (
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 110px)", background: "#f3f4f8", fontFamily: "system-ui" }}>
				<div style={{ textAlign: "center" }}>
					<div style={{ fontSize: 32, marginBottom: 12 }}>{"\ud83d\udce8"}</div>
					<div style={{ fontSize: 15, color: "#5f6478", fontWeight: 600 }}>Loading traffic instructions...</div>
				</div>
			</div>
		);
	}

	if (!sel || !draft) {
		return (
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 110px)", background: "#f3f4f8", fontFamily: "system-ui" }}>
				<div style={{ textAlign: "center" }}>
					<div style={{ fontSize: 32, marginBottom: 12 }}>{"\ud83d\udcec"}</div>
					<div style={{ fontSize: 15, color: "#5f6478", fontWeight: 600 }}>Select an email to get started</div>
				</div>
			</div>
		);
	}

	const sm = STATUS_META[sel.status] || STATUS_META.unprocessed;

	const isParsed = sel.conf === "ok" || sel.conf === "pushed";
	const showNew = sel.conf === "new";
	const showError = sel.conf === "error";
	const showParsed = isParsed;

	const STATUS_FILTERS = new Set(["unprocessed", "processed"]);

	const counts: Record<string, number> = {};
	dateWindowEmails.forEach((e) => {
		counts[e.status] = (counts[e.status] || 0) + 1;
		counts[e.label] = (counts[e.label] || 0) + 1;
	});

	const q = search.trim().toLowerCase();
	let list = dateWindowEmails.filter((e) =>
		STATUS_FILTERS.has(activeFilter) ? e.status === activeFilter : e.label === activeFilter
	);
	if (q) list = list.filter((e) => (e.advertiser + " " + e.agency + " " + e.network + " " + e.campaign + " " + e.subject).toLowerCase().includes(q));

	// Sort by received date descending (most recent first)
	list.sort((a, b) => new Date(b.received).getTime() - new Date(a.received).getTime());

	// Dedup by PDF hash — emails are most-recent-first, so first seen wins
	const seenHashes = new Set<string>();
	list = list.filter(e => {
		if (!e.pdfHash) return true;
		if (seenHashes.has(e.pdfHash)) return false;
		seenHashes.add(e.pdfHash);
		return true;
	});

	const fields = [
		{ key: "agency" as const, label: "Agency" },
		{ key: "advertiser" as const, label: "Advertiser" },
		{ key: "brand" as const, label: "Brand" },
		{ key: "campaign" as const, label: "Campaign #" },
		{ key: "network" as const, label: "Network" },
	];

	const curExcluded = (selectedId && excludedGroups[selectedId]) || new Set<string>();
	const curExcludedItems = (selectedId && excludedItems[selectedId]) || new Set<string>();
	const toggleItem = (key: string) => {
		if (!selectedId) return;
		setExcludedItems((prev) => {
			const s = new Set(prev[selectedId] || []);
			if (s.has(key)) s.delete(key); else s.add(key);
			return { ...prev, [selectedId]: s };
		});
	};

	// Comparison is only valid for the deal it was run against
	const comparison = sel.sfComparison && sel.sfComparison.dealId === sel.selectedDealId ? sel.sfComparison : null;
	const compareByKey = new Map((comparison?.items || []).map((c) => [c.key, c]));

	let itemCount = 0;
	const groups = (sel.groups || []).flatMap((g, gi) => {
		// Sub-group items by length within each daypart — rotation should
		// sum to 100% per daypart per length (e.g. :30s and :60s each sum
		// to 100% independently).
		const itemsByLength = new Map<number, { it: SpotItem; ii: number }[]>();
		g.items.forEach((it, ii) => {
			const len = it.length || 0;
			if (!itemsByLength.has(len)) itemsByLength.set(len, []);
			itemsByLength.get(len)!.push({ it, ii });
		});

		return Array.from(itemsByLength.entries()).map(([length, entries]) => {
			let total = 0;
			const items = entries.map(({ it, ii }) => {
				const key = gi + ":" + ii;
				const rv = draft.rots[key] ?? it.rot;
				const included = !curExcludedItems.has(key);
				// Rotation total covers only the splits that will be pushed
				if (included) total += Number(rv) || 0;
				itemCount++;
				const phone = draft.phones[key] ?? it.tollFreeNumber ?? "";
				const dates = draft.spotDates[key] ?? { start: it.startDate || "", end: it.endDate || "" };
				return { ...it, rot: rv, rotKey: key, phone, spotStart: dates.start, spotEnd: dates.end, included };
			});
			const good = total === 100;
			const label = itemsByLength.size > 1 ? `${g.daypart} :${length}` : g.daypart;
			const groupKey = `${g.daypart}|${length}`;
			return { daypart: label, window: g.window, items, total, length, groupKey, totalBg: good ? "#e4f6ee" : "#fdf1e0", totalColor: good ? "#12996a" : "#c98a13" };
		});
	});

	// Sort groups: matching the linked deal's length first, non-matching at bottom.
	const allDeals = [...(sel.dealMatches || [])];
	const selectedDeal = sel.selectedDealId ? allDeals.find(d => d.dealId === sel.selectedDealId) : null;
	const dealLength = selectedDeal?.length ? parseInt(String(selectedDeal.length), 10) : null;
	if (dealLength) {
		groups.sort((a, b) => {
			const aMatch = a.length === dealLength ? 0 : 1;
			const bMatch = b.length === dealLength ? 0 : 1;
			return aMatch - bMatch;
		});
	}

	const meta = [
		{ k: "From", v: sel.from },
		{ k: "Received", v: sel.received },
		{ k: "File", v: sel.pdfName || "(no PDF)" },
		{ k: "Size", v: sel.size },
	];

	let extractIcon: string, extractText: string, extractBg: string, extractBorder: string, extractColor: string;
	if (showError) {
		extractIcon = "\u25d4"; extractText = "Parse incomplete \u2014 will retry on next sync."; extractBg = "#fbf1de"; extractBorder = "#e8d5a8"; extractColor = "#92700e";
	} else if (showNew) {
		extractIcon = "\u25d4"; extractText = "Not yet extracted. Run the parser to pull fields & spots."; extractBg = "#eeedfe"; extractBorder = "#d7d5fb"; extractColor = "#4338ca";
	} else {
		const fCount = [draft.agency, draft.advertiser, draft.campaign, draft.network, draft.flightStart].filter(Boolean).length;
		extractIcon = "\u2713"; extractText = `Extracted ${fCount} fields \u00b7 ${itemCount} line-item spots.`; extractBg = "#e4f6ee"; extractBorder = "#bfe6d4"; extractColor = "#127a56";
	}

	let primaryLabel: string, primaryBg: string, primaryArrow: string, onPrimary: () => void, footerHint: string;
	// Cancellations have nothing to push: ops cancel with an LTC on the schedules
	const noPush = !showError && !showNew && sel.status !== "processed" && sel.label === "cancellation";
	if (showError) {
		primaryLabel = "Push to Salesforce"; primaryBg = "#9aa0b4"; primaryArrow = "\u2192";
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onPrimary = () => {}; footerHint = "Will retry on next sync";
	} else if (showNew) {
		primaryLabel = parsing ? "Parsing..." : "Run parser"; primaryBg = "#4f46e5"; primaryArrow = "\u2192"; onPrimary = runParse; footerHint = "Not parsed yet";
	} else if (sel.status === "processed") {
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		primaryLabel = "Processed"; primaryBg = "#9aa0b4"; primaryArrow = "\u2713"; onPrimary = () => { /* noop */ }; footerHint = "Already in Salesforce";
	} else if (noPush) {
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		primaryLabel = ""; primaryBg = ""; primaryArrow = ""; onPrimary = () => {};
		footerHint = sel.selectedDealId ? "Cancellation: check the LTC box, then Mark done" : "Cancellation: link a deal to check the LTC";
	} else if (!sel.selectedDealId) {
		primaryLabel = "Push to Salesforce"; primaryBg = "#9aa0b4"; primaryArrow = "\u2192";
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onPrimary = () => {}; footerHint = "Link a deal first";
	} else if (!sel.trafficId) {
		primaryLabel = "Create Traffic first"; primaryBg = "#9aa0b4"; primaryArrow = "\u2192";
		onPrimary = createTraffic; footerHint = "No Traffic record on deal";
	} else {
		primaryLabel = "Push to Salesforce"; primaryBg = "#4f46e5"; primaryArrow = "\u2192";
		onPrimary = () => { setModalOpen(true); setPhase("confirm"); setPushError(null); };
		footerHint = "Review, then commit";
	}

	// A revision only writes what changed: superseded splits end the day before the new terms start
	const plan = sel.label !== "new" && sel.label !== "cancellation" ? sel.sfComparison?.plan : undefined;
	const endOn = Array.from(new Set(plan?.endDates.map((e) => e.endDate) || [])).sort().join(", ");
	const done = (title: string, detail: string) => ({ mark: "\u2713", title, detail, badgeBg: "#e4f6ee", badgeColor: "#12996a" });
	const step = (mark: string, title: string, detail: string) => ({ mark, title, detail, badgeBg: "#eeedfe", badgeColor: "#4f46e5" });
	const flowSteps =
		phase === "done" && sel.label === "cancellation"
			? [done("Cancellation recorded", "No Salesforce writes; splits stay as they are")]
			: phase === "done"
			? [
					done(`${pushResult?.splitsCreated ?? 0} split(s) created`, `On ${sel.trafficName || "the traffic record"}`),
					...(pushResult?.splitsEnded ? [done(`${pushResult.splitsEnded} split(s) ended`, endOn ? `End date set to ${endOn}` : "End date moved")] : []),
					...(pushResult?.splitsDeleted ? [done(`${pushResult.splitsDeleted} split(s) deleted`, "Superseded before they started airing")] : []),
				]
			: sel.label === "cancellation"
			? [
					step("\u2192", "No Salesforce writes", "Splits stay as they are; ops cancel the order with an LTC on the deal's schedules"),
					step("\u2192", sel.sfComparison?.ltc?.status === "done" ? "LTC already entered" : sel.sfComparison?.ltc?.status === "pending" ? "LTC not entered yet" : "LTC status unknown", "Shown in the LTC box on the Details tab"),
				]
			: plan
			? [
					...(plan.endDates.length ? [step("\u2192", `End ${plan.endDates.length} existing split(s)`, `End date set to ${endOn}; weeks already aired keep their history`)] : []),
					...(plan.remove.length ? [step("\u2192", `Delete ${plan.remove.length} split(s)`, "Superseded before they started airing")] : []),
					step("\u2192", `Create ${plan.create.length} split(s)`, plan.create.length ? "New or changed line items" : "Nothing new to create"),
					step("\u2192", "Leave the rest as is", "Unchanged splits are not touched"),
				]
			: [
					{ mark: "1", title: "Match advertiser & campaign", detail: `${draft.advertiser} \u2192 Account \u00b7 #${draft.campaign}`, badgeBg: "#eeedfe", badgeColor: "#4f46e5" },
					{ mark: "2", title: `Create ${itemCount} line-item spots`, detail: `${groups.length} dayparts \u00b7 ISCI \u2192 creative`, badgeBg: "#eeedfe", badgeColor: "#4f46e5" },
					{ mark: "3", title: "Apply rotation splits", detail: "Validate each daypart sums to 100%", badgeBg: "#eeedfe", badgeColor: "#4f46e5" },
					{ mark: "4", title: "Run traffic decision flow", detail: "Revision & conflict handling, then commit", badgeBg: "#eeedfe", badgeColor: "#4f46e5" },
				];

	const syncAgo = lastSynced
		? `synced ${Math.max(0, Math.round((Date.now() - lastSynced.getTime()) / 60000))} min ago`
		: "not synced";

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 110px)", overflow: "hidden", fontFamily: "system-ui,-apple-system,'Helvetica Neue',Arial,sans-serif", color: "#232838", background: "#f3f4f8" }}>
			{/* Sub header */}
			<div style={{ flex: "none", display: "flex", alignItems: "center", gap: 16, background: "#fff", borderBottom: "1px solid #e6e7ef", padding: "14px 24px" }}>
				<div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(150deg,#4f46e5,#7c6cf0)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, boxShadow: "0 3px 10px rgba(79,70,229,.32)" }}>
					TI
				</div>
				<div>
					<h1 style={{ margin: 0, fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: "#1a1e2e", letterSpacing: "-.4px", lineHeight: 1.1 }}>Traffic Instructions</h1>
					<div style={{ fontSize: 12.5, color: "#8a8fa3", fontWeight: 500, marginTop: 1 }}>Gmail intake &middot; auto-parsed &rarr; Salesforce</div>
				</div>
				{/* Top-level view tabs */}
				<div style={{ display: "flex", gap: 0, marginLeft: 24 }}>
					{([["inbox", "Inbox"], ["log", "Activity Log"]] as const).map(([key, label]) => (
						<button key={key} onClick={() => setTopView(key)} style={{
							padding: "8px 16px", fontSize: 13, fontWeight: topView === key ? 700 : 500, cursor: "pointer",
							border: "none", background: "none", color: topView === key ? "#4f46e5" : "#8a8fa3",
							borderBottom: topView === key ? "2px solid #4f46e5" : "2px solid transparent",
							fontFamily: "'Sora',sans-serif",
						}}>{label}</button>
					))}
				</div>
				{topView === "inbox" && <>
				{/* Layout mode switcher - mini screen diagrams */}
				<div style={{ display: "flex", gap: 3, marginLeft: 16, background: "#f2f3f7", borderRadius: 7, padding: 3 }}>
					{([
						["default", "All panels", [true, true, true]],
						["emailCollapsed", "Email collapsed", [false, true, true]],
						["pdfCollapsed", "PDF collapsed", [true, true, false]],
						["workOnly", "Work area only", [false, true, false]],
					] as const).map(([mode, tip, panels]) => {
						const active = layoutMode === mode;
						const fg = active ? "#fff" : "#9aa0b4";
						const activeFill = active ? "rgba(255,255,255,.25)" : "#d0d2db";
						const workFill = active ? "rgba(255,255,255,.5)" : "#b8bbc8";
						return (
							<button
								key={mode}
								onClick={() => setLayoutMode(mode as any)}
								title={tip as string}
								style={{
									padding: 4, borderRadius: 5, cursor: "pointer", border: "none",
									background: active ? "#4f46e5" : "transparent", display: "flex", alignItems: "center",
								}}
							>
								<svg width="24" height="16" viewBox="0 0 24 16">
									<rect x="0.5" y="0.5" width="23" height="15" rx="2" fill="none" stroke={fg} strokeWidth="1" />
									{/* Left panel (inbox) */}
									{panels[0] && <><rect x="1" y="1" width="6" height="14" rx="1" fill={activeFill} /><line x1="7.5" y1="1" x2="7.5" y2="15" stroke={fg} strokeWidth="0.7" /></>}
									{/* Center panel (work area) - stretches to fill */}
									{(() => {
										const x = panels[0] ? 8.5 : 1;
										const end = panels[2] ? 15.5 : 23;
										return <rect x={x} y="1" width={end - x} height="14" rx="1" fill={workFill} />;
									})()}
									{/* Right panel (PDF) */}
									{panels[2] && <><line x1="16.5" y1="1" x2="16.5" y2="15" stroke={fg} strokeWidth="0.7" /><rect x="17" y="1" width="6" height="14" rx="1" fill={activeFill} /></>}
								</svg>
							</button>
						);
					})}
				</div>
				<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#5f6478", fontWeight: 500 }}>
					<span style={{ width: 8, height: 8, borderRadius: "50%", background: lastSynced ? "#4f46e5" : "#9aa0b4", display: "inline-block", boxShadow: lastSynced ? "0 0 0 3px rgba(79,70,229,.16)" : "none" }} />
					Mailbox connected &middot; {syncAgo}
				</div>
				<select
					value={datePreset}
					onChange={(e) => { setDatePreset(Number(e.target.value)); }}
					style={{ border: "1px solid #d3d6e4", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#232838", padding: "8px 12px", background: "#fff", fontFamily: "'Sora',sans-serif", cursor: "pointer" }}
				>
					{DATE_PRESETS.map((p, i) => (
						<option key={i} value={i}>{p.label}</option>
					))}
				</select>
				<button
					onClick={() => syncEmails(true)}
					disabled={syncing}
					style={{ display: "inline-flex", alignItems: "center", gap: 7, background: syncing ? "#9aa0b4" : "#4f46e5", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#fff", padding: "9px 15px", cursor: syncing ? "default" : "pointer", fontFamily: "'Sora',sans-serif" }}
				>
					&#8635; {syncing ? "Syncing..." : "Sync now"}
				</button>
				</>}
			</div>

			{/* Activity Log view */}
			{topView === "log" && <ActivityLog />}

			{/* Workspace */}
			{topView === "inbox" && <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
				{/* Left: inbox list (collapsible) */}
				{inboxOpen ? (
				<aside
					style={{ flex: "none", width: "clamp(260px, 24vw, 360px)", background: "#fff", borderRight: "1px solid #e6e7ef", display: "flex", flexDirection: "column", minHeight: 0 }}>
					<div style={{ flex: "none", padding: "14px 16px 11px", borderBottom: "1px solid #eef0f4" }}>
						<div style={{ display: "flex", alignItems: "center", marginBottom: 11 }}>
							<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase" }}>Inbox</span>
							<button onClick={() => togglePanel("inbox", false)} style={{ marginLeft: "auto", border: "1px solid #dcdfea", background: "#fff", borderRadius: 7, width: 28, height: 28, cursor: "pointer", color: "#8a8fa3", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }} title="Collapse inbox">&#8676;</button>
						</div>
						<div style={{ position: "relative", marginBottom: 11 }}>
							<span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#9aa0b4", fontSize: 14 }}>{"\u2315"}</span>
							<input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search agency, advertiser, campaign\u2026" style={{ width: "100%", padding: "9px 12px 9px 30px", border: "1px solid #dcdfea", borderRadius: 9, fontSize: 13, outline: "none", color: "#232838" }} />
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
							{FILTER_KEYS.map(([k, lbl]) => {
								const active = activeFilter === k;
								return (
									<button key={k} onClick={() => setActiveFilter(k)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${active ? "#4f46e5" : "#e4e6ef"}`, background: active ? "#4f46e5" : "#f2f3f7", color: active ? "#fff" : "#5f6478", fontFamily: "'Sora',sans-serif" }}>
										{lbl}<span style={{ fontSize: 11, opacity: 0.75 }}>{counts[k] || 0}</span>
									</button>
								);
							})}
						</div>
					</div>

					<div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
						{list.map((e) => {
							const esm = STATUS_META[e.status] || STATUS_META.unprocessed;
							const isSel = e.id === selectedId;
							return (
								<div key={e.id} onClick={() => select(e.id)} style={{ padding: "13px 16px", borderBottom: "1px solid #f2f3f7", cursor: "pointer", borderLeft: `3px solid ${isSel ? "#4f46e5" : "transparent"}`, background: isSel ? "#f5f5fe" : e.unread ? "#fcfcfe" : "#fff" }}>
									<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
										{e.unread && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4f46e5", display: "inline-block", flex: "none" }} />}
										<LabelBadge email={e} padding="2px 7px" />
										<span style={{ marginLeft: "auto", fontSize: 11, color: "#9aa0b4", flex: "none" }}>{e.receivedShort}</span>
									</div>
									<div style={{ fontSize: 13, color: "#333a52", lineHeight: 1.35, marginBottom: 6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{e.subject}</div>
									<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
										<span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: esm.bg, color: esm.color }}>
											<span style={{ width: 6, height: 6, borderRadius: "50%", background: esm.color, display: "inline-block" }} />{esm.label}
										</span>
										{e.agency && <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 700, color: "#1a1e2e" }}>{e.agency}</span>}
									</div>
									<div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", fontSize: 12, color: "#5f6478" }}>
										{e.network && <span>{e.network}</span>}
										{e.flight && <span>{e.flight}</span>}
									</div>
								</div>
							);
						})}
						<div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "#b7bcca" }}>{list.length} of {emails.length} instructions</div>
					</div>
				</aside>
				) : (
				<div
					onClick={() => togglePanel("inbox", true)}
					style={{ flex: "none", width: 6, background: "linear-gradient(180deg, #4f46e5 0%, #7c74f0 100%)", borderRight: "1px solid #e6e7ef", cursor: "pointer", position: "relative" }}
					title="Click to show inbox"
				>
					<div style={{ position: "absolute", top: 12, left: -9, width: 24, height: 24, borderRadius: "50%", background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(79,70,229,.4)" }}>
						<span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>{emails.length}</span>
					</div>
				</div>
				)}

				{/* Center: interpretation */}
				<section style={{ flex: "1.4 1 0", display: "flex", flexDirection: "column", minWidth: 380, background: "#f3f4f8", minHeight: 0 }}>
					<div style={{ flex: "none", display: "flex", alignItems: "center", gap: 12, padding: "13px 24px", background: "#fff", borderBottom: "1px solid #e6e7ef" }}>
						<div style={{ minWidth: 0 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
								<LabelBadge email={sel} padding="2px 8px" />
							</div>
							<div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: "#1a1e2e", lineHeight: 1.1 }}>
								{sel.advertiser || sel.agency || "Unprocessed"} {sel.network ? `\u00b7 ${sel.network}` : ""}
							</div>
						</div>
						<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
							<a
								href={`https://mail.google.com/mail/u/0/#inbox/${sel.id}`}
								target="_blank"
								rel="noopener noreferrer"
								style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "1px solid #dcdfea", background: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#5f6478", padding: "5px 10px", textDecoration: "none", fontFamily: "'Sora',sans-serif" }}
								title="Open in Gmail"
							>
								&#9993; Gmail
							</a>
							<span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 7, fontSize: 12, fontWeight: 700, background: sm.bg, color: sm.color, fontFamily: "'Sora',sans-serif" }}>
								<span style={{ width: 6, height: 6, borderRadius: "50%", background: sm.color, display: "inline-block" }} />{sm.label}
							</span>
							{!sourceOpen && (
								<button onClick={() => togglePanel("source", true)} style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 8, fontSize: 12.5, fontWeight: 600, color: "#4f46e5", padding: "7px 12px", cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>
									&#128196; View source
								</button>
							)}
						</div>
					</div>

					{/* Tabs */}
					{(showParsed || showError) && (
						<div style={{ flex: "none", display: "flex", gap: 0, background: "#fff", borderBottom: "1px solid #e6e7ef", padding: "0 24px" }}>
							{([["details", "Details"], ["lineItems", `Line Items${itemCount > 0 ? ` (${itemCount})` : ""}`]] as const).map(([key, label]) => (
								<button
									key={key}
									onClick={() => setActiveTab(key)}
									style={{
										padding: "10px 18px", fontSize: 13, fontWeight: activeTab === key ? 700 : 500, cursor: "pointer",
										border: "none", background: "none", color: activeTab === key ? "#4f46e5" : "#8a8fa3",
										borderBottom: activeTab === key ? "2px solid #4f46e5" : "2px solid transparent",
										fontFamily: "'Sora',sans-serif", marginBottom: -1,
									}}
								>
									{label}
								</button>
							))}
						</div>
					)}

					<div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "22px 24px 26px" }}>
						<div style={{ maxWidth: 780, margin: "0 auto" }}>
							{showNew && (
								<div style={{ textAlign: "center", padding: "46px 20px", border: "1.5px dashed #d3d6e4", borderRadius: 14, background: "#fff" }}>
									<div style={{ fontSize: 34, marginBottom: 10 }}>{"\ud83d\uddc2\ufe0f"}</div>
									<div style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 700, color: "#1a1e2e", marginBottom: 5 }}>This instruction hasn&apos;t been parsed yet</div>
									<div style={{ fontSize: 13, color: "#8a8fa3", maxWidth: 380, margin: "0 auto 18px", lineHeight: 1.5 }}>Run the parser to extract campaign details and line items from the attached PDF, then review before pushing to Salesforce.</div>
									<button onClick={runParse} disabled={parsing} style={{ border: "none", background: parsing ? "#9aa0b4" : "#4f46e5", color: "#fff", borderRadius: 9, fontSize: 13.5, fontWeight: 700, padding: "11px 22px", cursor: parsing ? "default" : "pointer", fontFamily: "'Sora',sans-serif" }}>
										{parsing ? "Parsing..." : "Run parser"}
									</button>
								</div>
							)}

							{(showParsed || showError) && activeTab === "details" && (
								<>
									{/* ── Unified Deal Section ── */}
									<DealSection
										sel={sel}
										dealSearchQuery={dealSearchQuery}
										dealSearchResults={dealSearchResults}
										dealSearching={dealSearching}
										handleDealSearch={handleDealSearch}
										selectDeal={(deal) => { selectDeal(deal); setDealSearchQuery(""); setDealSearchResults([]); }}
										sfInstanceUrl={sfInstanceUrl}
									/>

									<div style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase", marginBottom: 10 }}>
										Campaign details <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "#b7bcca" }}>&middot; {sel.selectedDealId ? "from Salesforce deal" : "click any field to edit"}</span>
									</div>
									<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "11px 14px", marginBottom: 24 }}>
										{fields.map((fld) => (
											<label key={fld.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
												<span style={{ fontSize: 11, color: "#8a8fa3", fontWeight: 600 }}>{fld.label}</span>
												<input value={draft[fld.key] ?? ""} onChange={(e) => setField(fld.key, e.target.value)} style={{ width: "100%", minWidth: 0, boxSizing: "border-box", border: "1px solid #dcdfea", borderRadius: 8, padding: "9px 11px", fontSize: 13, color: "#1a1e2e", outline: "none", fontWeight: 600, background: "#fff" }} />
											</label>
										))}
									</div>

									{groups.length > 0 && (
										<>
											<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
												<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase" }}>Line items &rarr; Salesforce spots</span>
												<button
													onClick={() => {
														if (!selectedId) return;
														const allExcluded = groups.every(g => curExcluded.has(g.groupKey));
														setExcludedGroups((prev) => {
															const s = new Set<string>();
															if (!allExcluded) groups.forEach(g => s.add(g.groupKey));
															return { ...prev, [selectedId]: s };
														});
														setExcludedItems((prev) => ({ ...prev, [selectedId]: new Set<string>() }));
													}}
													style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 700, color: "#5f6478", cursor: "pointer", fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" }}
												>
													{groups.every(g => curExcluded.has(g.groupKey)) ? "Select all" : "Deselect all"}
												</button>
												<span style={{ marginLeft: "auto", fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 700, color: "#5f6478" }}>
													{groups.filter(g => !curExcluded.has(g.groupKey)).reduce((n, g) => n + g.items.filter(it => it.included).length, 0)} included &middot; {groups.length} sections
												</span>
											</div>

											{groups.map((g, gi) => {
											const isExcluded = curExcluded.has(g.groupKey);
											return (
												<div key={gi} style={{ border: `1px solid ${isExcluded ? "#f0e0d0" : "#e9ebf1"}`, borderRadius: 12, marginBottom: 13, overflow: "hidden", background: isExcluded ? "#fdfaf7" : "#fff", opacity: isExcluded ? 0.55 : 1 }}>
													<div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 14px", background: isExcluded ? "#faf6f2" : "#fafbfd", borderBottom: "1px solid #eef0f4" }}>
														<button
															onClick={() => {
																if (!selectedId) return;
																setExcludedGroups((prev) => {
																	const s = new Set(prev[selectedId] || []);
																	if (s.has(g.groupKey)) s.delete(g.groupKey); else s.add(g.groupKey);
																	return { ...prev, [selectedId]: s };
																});
															}}
															style={{
																width: 20, height: 20, borderRadius: 5, border: `2px solid ${isExcluded ? "#d0d3df" : "#4f46e5"}`,
																background: isExcluded ? "#fff" : "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center",
																cursor: "pointer", flex: "none", fontSize: 11, color: "#fff", fontWeight: 800, padding: 0,
															}}
															title={isExcluded ? "Include in push" : "Exclude from push"}
														>
															{isExcluded ? "" : "\u2713"}
														</button>
														<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10, fontWeight: 700, color: isExcluded ? "#b7bcca" : "#9aa0b4", minWidth: 18 }}>{gi + 1}.</span>
														<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 800, color: isExcluded ? "#9aa0b4" : "#1a1e2e" }}>{g.daypart}</span>
														<span style={{ fontSize: 11.5, color: "#8a8fa3" }}>{g.window}</span>
														{isExcluded && (
															<span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: "#fdf1e0", color: "#c98a13", fontFamily: "'Sora',sans-serif" }}>
																Excluded from push
															</span>
														)}
														<span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6, background: g.totalBg, color: g.totalColor, fontFamily: "'Sora',sans-serif" }}>&Sigma; {g.total}%</span>
													</div>
													<div style={{ display: "grid", gridTemplateColumns: "26px minmax(96px,1.4fr) minmax(0,1.1fr) 46px 74px", gap: "0 8px", fontSize: 10.5, fontWeight: 700, color: "#9aa0b4", padding: "7px 14px", borderBottom: "1px solid #f2f3f7", textTransform: "uppercase", letterSpacing: ".4px" }}>
														<span /><span>ISCI / Ad-ID</span><span>Title</span><span style={{ textAlign: "center" }}>Len</span><span style={{ textAlign: "right" }}>Rot %</span>
													</div>
													{g.items.map((it, ii) => (
														<div key={ii} style={{ borderBottom: "1px solid #f5f6f9", opacity: it.included ? 1 : 0.45 }}>
															<div style={{ display: "grid", gridTemplateColumns: "26px minmax(96px,1.4fr) minmax(0,1.1fr) 46px 74px", gap: "0 8px", alignItems: "center", padding: "6px 14px" }}>
																<button
																	onClick={() => toggleItem(it.rotKey)}
																	style={{
																		width: 16, height: 16, borderRadius: 4, border: `2px solid ${it.included ? "#4f46e5" : "#d0d3df"}`,
																		background: it.included ? "#4f46e5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center",
																		cursor: "pointer", fontSize: 9, color: "#fff", fontWeight: 800, padding: 0,
																	}}
																	title={it.included ? "Exclude this split from push" : "Include this split in push"}
																>
																	{it.included ? "\u2713" : ""}
																</button>
																<span title={it.isci} style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11.5, color: "#1a1e2e", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.isci}</span>
																<span style={{ fontSize: 12, color: "#5f6478", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
																<span style={{ fontSize: 12, color: "#8a8fa3", textAlign: "center" }}>{it.length}s</span>
																<span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
																	<input value={it.rot} onChange={(e) => setRot(it.rotKey, e.target.value)} style={{ width: 44, border: changedBorder(compareByKey.get(it.rotKey), "rot"), borderRadius: 6, padding: "4px 6px", fontSize: 12, textAlign: "right", color: "#1a1e2e", fontWeight: 600, outline: "none" }} />
																	<span style={{ fontSize: 11, color: "#9aa0b4" }}>%</span>
																</span>
															</div>
															<div style={{ display: "flex", flexWrap: "wrap", gap: 8, rowGap: 6, padding: "2px 14px 6px", alignItems: "center" }}>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><label style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, letterSpacing: ".3px" }}>800#</label>
																<input value={it.phone} onChange={(e) => setPhone(it.rotKey, e.target.value)} placeholder="-" style={{ width: 130, border: changedBorder(compareByKey.get(it.rotKey), "tollFreeNumber"), borderRadius: 6, padding: "3px 6px", fontSize: 11, color: "#1a1e2e", fontWeight: 500, outline: "none", fontFamily: "ui-monospace,Menlo,monospace" }} /></span>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><label style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, letterSpacing: ".3px" }}>Start</label>
																<input type="date" value={it.spotStart} onChange={(e) => setSpotDate(it.rotKey, "start", e.target.value)} style={{ border: changedBorder(compareByKey.get(it.rotKey), "startDate"), borderRadius: 6, padding: "3px 6px", fontSize: 11, color: it.spotStart ? "#1a1e2e" : "#9aa0b4", outline: "none" }} /></span>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><label style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, letterSpacing: ".3px" }}>End</label>
																<input type="date" value={it.spotEnd} onChange={(e) => setSpotDate(it.rotKey, "end", e.target.value)} style={{ border: changedBorder(compareByKey.get(it.rotKey), "endDate"), borderRadius: 6, padding: "3px 6px", fontSize: 11, color: it.spotEnd ? "#1a1e2e" : "#9aa0b4", outline: "none" }} /></span>
																<CompareChip item={compareByKey.get(it.rotKey)} />
															</div>
														</div>
													))}
													{g.items.length > 1 && (
														<div style={{ display: "flex", gap: 6, padding: "6px 14px", borderBottom: "1px solid #f2f3f7", alignItems: "center" }}>
															<span style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".3px" }}>Apply to all:</span>
															<button onClick={() => { const v = g.items[0]?.phone; if (v) applyToAllSpots("phone", v, gi); }} style={{ border: "1px solid #dcdfea", borderRadius: 5, background: "#f8f9fc", padding: "2px 8px", fontSize: 10, color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>800#</button>
															<button onClick={() => { const v = g.items[0]?.spotStart; if (v) applyToAllSpots("startDate", v, gi); }} style={{ border: "1px solid #dcdfea", borderRadius: 5, background: "#f8f9fc", padding: "2px 8px", fontSize: 10, color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>Start</button>
															<button onClick={() => { const v = g.items[0]?.spotEnd; if (v) applyToAllSpots("endDate", v, gi); }} style={{ border: "1px solid #dcdfea", borderRadius: 5, background: "#f8f9fc", padding: "2px 8px", fontSize: 10, color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>End</button>
														</div>
													)}
												</div>
											);
											})}
										</>
									)}

									{/* Traffic record status — show when deal is linked */}
									{sel.selectedDealId && (
										<div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 15px", borderRadius: 11, marginBottom: 12, background: sel.trafficId ? "#e4f6ee" : "#fbf1de", border: `1px solid ${sel.trafficId ? "#bfe6d4" : "#e8d5a8"}` }}>
											<span style={{ fontSize: 15 }}>{sel.trafficId ? "\u2713" : "\u26a0\ufe0f"}</span>
											<div style={{ flex: 1 }}>
												<span style={{ fontSize: 12.5, fontWeight: 700, color: sel.trafficId ? "#127a56" : "#92700e" }}>
													{sel.trafficId ? `Traffic record: ${sel.trafficName || sel.trafficId}` : "No Traffic record on this deal"}
												</span>
												{!sel.trafficId && (
													<span style={{ fontSize: 11.5, color: "#92700e", marginLeft: 6 }}>— create one to push spots</span>
												)}
											</div>
											{!sel.trafficId && (
												<button
													onClick={createTraffic}
													disabled={creatingTraffic}
													style={{ border: "none", background: "#4f46e5", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700, padding: "7px 14px", cursor: creatingTraffic ? "default" : "pointer", fontFamily: "'Sora',sans-serif" }}
												>
													{creatingTraffic ? "Creating..." : "Create Traffic"}
												</button>
											)}
										</div>
									)}
									{trafficError && (
										<div style={{ padding: "10px 14px", borderRadius: 8, background: "#fdecef", border: "1px solid #f3ccd3", color: "#b73145", fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
											Create failed: {trafficError}
										</div>
									)}
									{comparison?.ltc && (() => {
										const l = comparison.ltc;
										const from = l.lastAirDate ? `after ${l.lastAirDate}` : "(cancelled before start)";
										const tone = l.status === "pending" ? { bg: "#fef3c7", border: "#f5d27a", color: "#92400e" } : l.status === "done" ? { bg: "#e4f6ee", border: "#bfe8d5", color: "#0e7a54" } : { bg: "#fafbfd", border: "#e9ebf1", color: "#5f6478" };
										return (
											<div style={{ padding: "11px 15px", borderRadius: 11, marginBottom: 12, background: tone.bg, border: `1px solid ${tone.border}` }}>
												<div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 12.5, fontWeight: 700, color: tone.color }}>
													<span>
														{l.status === "done" && `LTC entered: all ${l.weeksChecked} schedule weeks ${from} are Canceled`}
														{l.status === "pending" && `LTC not entered: ${l.notCanceled.length} of ${l.weeksChecked} schedule weeks ${from} are still ${l.notCanceled[0]?.status || "Sold"}`}
														{l.status === "no_schedules" && `No schedule weeks ${from} to cancel`}
													</span>
													{l.status === "pending" && sfInstanceUrl && (
														<a href={`${sfInstanceUrl}/${comparison.dealId}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: "auto", fontSize: 11.5, color: "#4f46e5" }}>Open deal in Salesforce</a>
													)}
												</div>
												{l.status === "pending" && (
													<>
														<div style={{ fontSize: 11.5, color: "#5f6478", margin: "4px 0 6px" }}>Ops enter this on the deal&apos;s Double Check Report (LTC tab). Splits stay as they are.</div>
														{l.notCanceled.slice(0, 6).map((w) => (
															<div key={w.name} style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 11.5, color: "#5f6478", padding: "1px 0" }}>
																<span style={{ fontFamily: "ui-monospace,Menlo,monospace", color: "#1a1e2e" }}>{w.week}</span>
																<span>{w.daypart}</span>
																<span style={{ marginLeft: "auto" }}>{w.units} units &middot; {w.status}</span>
															</div>
														))}
														{l.notCanceled.length > 6 && <div style={{ fontSize: 11, color: "#8a8fa3" }}>+{l.notCanceled.length - 6} more</div>}
													</>
												)}
											</div>
										);
									})()}
									{/* On a whole-order cancellation every split goes, so "not in the instruction" is noise */}
									{comparison && comparison.missing.length > 0 && !comparison.ltc && (
										<div style={{ padding: "11px 15px", borderRadius: 11, marginBottom: 12, background: "#fafbfd", border: "1px solid #e9ebf1" }}>
											<div style={{ fontSize: 12.5, fontWeight: 700, color: "#333a52", marginBottom: 6 }}>
												{comparison.missing.length} SF split{comparison.missing.length === 1 ? "" : "s"} in this flight not in the instruction
											</div>
											{comparison.missing.map((m) => (
												<div key={m.splitId} style={{ display: "flex", gap: 10, fontSize: 11.5, color: "#5f6478", padding: "2px 0" }}>
													<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontWeight: 600, color: "#1a1e2e" }}>{m.isci}</span>
													<span>{m.spotName}</span>
													<span>{m.days}</span>
													{m.inFlight && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#b45309", background: "#fef3c7", borderRadius: 4, padding: "0 6px" }}>Dropped during flight</span>}
													<span style={{ marginLeft: "auto" }}>{m.startDate || "-"} &ndash; {m.endDate || "-"}</span>
												</div>
											))}
										</div>
									)}
								</>
							)}

							{/* ── Line Items Tab ── */}
							{(showParsed || showError) && activeTab === "lineItems" && (
								<>
									{groups.length === 0 ? (
										<div style={{ textAlign: "center", padding: "40px 20px", border: "1.5px dashed #d3d6e4", borderRadius: 14, background: "#fff" }}>
											<div style={{ fontSize: 28, marginBottom: 10 }}>{"\ud83d\udcdd"}</div>
											<div style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 700, color: "#1a1e2e", marginBottom: 5 }}>No line items extracted</div>
											<div style={{ fontSize: 13, color: "#8a8fa3", maxWidth: 400, margin: "0 auto", lineHeight: 1.5 }}>
												The parser didn&apos;t find ISCI codes or spot data in this PDF. The traffic instruction may use a format that isn&apos;t supported yet, or the PDF may be a scanned image.
											</div>
										</div>
									) : (
										<>
											<div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
												<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase" }}>
													Parsed line items
												</span>
												<span style={{ marginLeft: "auto", fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 700, color: "#5f6478" }}>
													{itemCount} spots &middot; {groups.length} dayparts
												</span>
											</div>

											{groups.map((g, gi) => {
											const isExcluded = curExcluded.has(g.groupKey);
											return (
												<div key={gi} style={{ border: `1px solid ${isExcluded ? "#f0e0d0" : "#e9ebf1"}`, borderRadius: 12, marginBottom: 13, overflow: "hidden", background: isExcluded ? "#fdfaf7" : "#fff", opacity: isExcluded ? 0.55 : 1 }}>
													<div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 14px", background: isExcluded ? "#faf6f2" : "#fafbfd", borderBottom: "1px solid #eef0f4" }}>
														<button
															onClick={() => {
																if (!selectedId) return;
																setExcludedGroups((prev) => {
																	const s = new Set(prev[selectedId] || []);
																	if (s.has(g.groupKey)) s.delete(g.groupKey); else s.add(g.groupKey);
																	return { ...prev, [selectedId]: s };
																});
															}}
															style={{
																width: 20, height: 20, borderRadius: 5, border: `2px solid ${isExcluded ? "#d0d3df" : "#4f46e5"}`,
																background: isExcluded ? "#fff" : "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center",
																cursor: "pointer", flex: "none", fontSize: 11, color: "#fff", fontWeight: 800, padding: 0,
															}}
															title={isExcluded ? "Include in push" : "Exclude from push"}
														>
															{isExcluded ? "" : "\u2713"}
														</button>
														<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10, fontWeight: 700, color: isExcluded ? "#b7bcca" : "#9aa0b4", minWidth: 18 }}>{gi + 1}.</span>
														<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 800, color: isExcluded ? "#9aa0b4" : "#1a1e2e" }}>{g.daypart}</span>
														<span style={{ fontSize: 11.5, color: "#8a8fa3" }}>{g.window}</span>
														{isExcluded && (
															<span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: "#fdf1e0", color: "#c98a13", fontFamily: "'Sora',sans-serif" }}>
																Excluded from push
															</span>
														)}
														<span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6, background: g.totalBg, color: g.totalColor, fontFamily: "'Sora',sans-serif" }}>&Sigma; {g.total}%</span>
													</div>
													<div style={{ display: "grid", gridTemplateColumns: "26px minmax(96px,1.4fr) minmax(0,1.1fr) 46px 74px", gap: "0 8px", fontSize: 10.5, fontWeight: 700, color: "#9aa0b4", padding: "7px 14px", borderBottom: "1px solid #f2f3f7", textTransform: "uppercase", letterSpacing: ".4px" }}>
														<span /><span>ISCI / Ad-ID</span><span>Title</span><span style={{ textAlign: "center" }}>Len</span><span style={{ textAlign: "right" }}>Rot %</span>
													</div>
													{g.items.map((it, ii) => (
														<div key={ii} style={{ borderBottom: "1px solid #f5f6f9", opacity: it.included ? 1 : 0.45 }}>
															<div style={{ display: "grid", gridTemplateColumns: "26px minmax(96px,1.4fr) minmax(0,1.1fr) 46px 74px", gap: "0 8px", alignItems: "center", padding: "6px 14px" }}>
																<button
																	onClick={() => toggleItem(it.rotKey)}
																	style={{
																		width: 16, height: 16, borderRadius: 4, border: `2px solid ${it.included ? "#4f46e5" : "#d0d3df"}`,
																		background: it.included ? "#4f46e5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center",
																		cursor: "pointer", fontSize: 9, color: "#fff", fontWeight: 800, padding: 0,
																	}}
																	title={it.included ? "Exclude this split from push" : "Include this split in push"}
																>
																	{it.included ? "\u2713" : ""}
																</button>
																<span title={it.isci} style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11.5, color: "#1a1e2e", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.isci}</span>
																<span style={{ fontSize: 12, color: "#5f6478", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
																<span style={{ fontSize: 12, color: "#8a8fa3", textAlign: "center" }}>{it.length}s</span>
																<span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
																	<input value={it.rot} onChange={(e) => setRot(it.rotKey, e.target.value)} style={{ width: 44, border: changedBorder(compareByKey.get(it.rotKey), "rot"), borderRadius: 6, padding: "4px 6px", fontSize: 12, textAlign: "right", color: "#1a1e2e", fontWeight: 600, outline: "none" }} />
																	<span style={{ fontSize: 11, color: "#9aa0b4" }}>%</span>
																</span>
															</div>
															<div style={{ display: "flex", flexWrap: "wrap", gap: 8, rowGap: 6, padding: "2px 14px 6px", alignItems: "center" }}>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><label style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, letterSpacing: ".3px" }}>800#</label>
																<input value={it.phone} onChange={(e) => setPhone(it.rotKey, e.target.value)} placeholder="-" style={{ width: 130, border: changedBorder(compareByKey.get(it.rotKey), "tollFreeNumber"), borderRadius: 6, padding: "3px 6px", fontSize: 11, color: "#1a1e2e", fontWeight: 500, outline: "none", fontFamily: "ui-monospace,Menlo,monospace" }} /></span>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><label style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, letterSpacing: ".3px" }}>Start</label>
																<input type="date" value={it.spotStart} onChange={(e) => setSpotDate(it.rotKey, "start", e.target.value)} style={{ border: changedBorder(compareByKey.get(it.rotKey), "startDate"), borderRadius: 6, padding: "3px 6px", fontSize: 11, color: it.spotStart ? "#1a1e2e" : "#9aa0b4", outline: "none" }} /></span>
																<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><label style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, letterSpacing: ".3px" }}>End</label>
																<input type="date" value={it.spotEnd} onChange={(e) => setSpotDate(it.rotKey, "end", e.target.value)} style={{ border: changedBorder(compareByKey.get(it.rotKey), "endDate"), borderRadius: 6, padding: "3px 6px", fontSize: 11, color: it.spotEnd ? "#1a1e2e" : "#9aa0b4", outline: "none" }} /></span>
																<CompareChip item={compareByKey.get(it.rotKey)} />
															</div>
														</div>
													))}
													{g.items.length > 1 && (
														<div style={{ display: "flex", gap: 6, padding: "6px 14px", borderBottom: "1px solid #f2f3f7", alignItems: "center" }}>
															<span style={{ fontSize: 10, color: "#9aa0b4", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".3px" }}>Apply to all:</span>
															<button onClick={() => { const v = g.items[0]?.phone; if (v) applyToAllSpots("phone", v, gi); }} style={{ border: "1px solid #dcdfea", borderRadius: 5, background: "#f8f9fc", padding: "2px 8px", fontSize: 10, color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>800#</button>
															<button onClick={() => { const v = g.items[0]?.spotStart; if (v) applyToAllSpots("startDate", v, gi); }} style={{ border: "1px solid #dcdfea", borderRadius: 5, background: "#f8f9fc", padding: "2px 8px", fontSize: 10, color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>Start</button>
															<button onClick={() => { const v = g.items[0]?.spotEnd; if (v) applyToAllSpots("endDate", v, gi); }} style={{ border: "1px solid #dcdfea", borderRadius: 5, background: "#f8f9fc", padding: "2px 8px", fontSize: 10, color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}>End</button>
														</div>
													)}
												</div>
											);
											})}
										</>
									)}
								</>
							)}
						</div>
					</div>

					{/* Action footer */}
					<div style={{ flex: "none", borderTop: "1px solid #e6e7ef", padding: "13px 24px", display: "flex", alignItems: "center", gap: 10, background: "#fff" }}>
						<button
							onClick={() => {
								const newStatus = sel.status === "processed" ? "unprocessed" : "processed";
								logTiAction({
									actionType: newStatus === "processed" ? "email_marked_done" : "email_unmarked_done",
									category: "navigation",
									description: `${newStatus === "processed" ? "Marked done" : "Unmarked"}: ${sel.subject}`,
									emailId: sel.id, emailSubject: sel.subject,
								});
								authAxios({ method: "PATCH", url: `${API}/salesforce/traffic-instructions/${sel.id}/state`, data: { status: newStatus } }).catch(() => { /* persist non-blocking */ });
								setEmails((prev) => prev.map((e) => e.id === sel.id ? { ...e, status: newStatus as "processed" | "unprocessed" } : e));
							}}
							style={{ border: `1px solid ${sel.status === "processed" ? "#bfe6d4" : "#dcdfea"}`, background: sel.status === "processed" ? "#e4f6ee" : "#fff", color: sel.status === "processed" ? "#12996a" : "#5f6478", borderRadius: 9, fontSize: 12, fontWeight: 700, padding: "10px 12px", cursor: "pointer", fontFamily: "'Sora',sans-serif" }}
						>
							{sel.status === "processed" ? "\u2713 Done" : "Mark done"}
						</button>
						{(sel.pushCount || 0) > 0 && (
							<span style={{ fontSize: 11, color: "#9aa0b4", fontWeight: 600 }}>
								{sel.pushCount} push{sel.pushCount === 1 ? "" : "es"}
							</span>
						)}
						<span style={{ fontSize: 12, color: "#9aa0b4" }}>{footerHint}</span>
						{!noPush && (
							<button onClick={onPrimary} disabled={parsing} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8, border: "none", background: primaryBg, color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 800, padding: "11px 22px", cursor: "pointer", fontFamily: "'Sora',sans-serif", boxShadow: "0 3px 10px rgba(79,70,229,.28)" }}>
								{primaryLabel} <span style={{ fontSize: 15 }}>{primaryArrow}</span>
							</button>
						)}
					</div>
				</section>

				{/* Right: PDF canvas panel */}
				{sourceOpen && (
					<aside style={{ flex: "1 1 0", minWidth: 240, maxWidth: "50%", background: "#e8e9ed", borderLeft: "1px solid #e6e7ef", display: "flex", flexDirection: "column", minHeight: 0 }}>
						<div style={{ flex: "none", padding: "10px 14px", borderBottom: "1px solid #dcdee4", display: "flex", alignItems: "center", gap: 10, background: "#f5f5f8" }}>
							<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".7px", color: "#9aa0b4", textTransform: "uppercase" }}>Source PDF</span>
							{sel.pdfName && <span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10, color: "#b7bcca", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sel.pdfName}</span>}
							<div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
								{pdfUrl && (
									<button onClick={() => setPdfFullscreen(true)} style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: "#5f6478", cursor: "pointer", fontFamily: "'Sora',sans-serif" }} title="Fullscreen">&#x26F6;</button>
								)}
								<button onClick={() => togglePanel("source", false)} style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 6, width: 26, height: 26, cursor: "pointer", color: "#8a8fa3", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }} title="Collapse">&#8677;</button>
							</div>
						</div>
						{pdfUrl ? (
							<PdfCanvas pdfUrl={pdfUrl} />
						) : (
							<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#9aa0b4", fontSize: 13 }}>
								{sel.pdfName ? "Loading PDF..." : "No PDF attached"}
							</div>
						)}
					</aside>
				)}
			</div>}

			{/* Push flow modal */}
			{modalOpen && (
				<div onClick={() => setModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(20,22,40,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
					<div onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: "92vw", background: "#fff", borderRadius: 15, boxShadow: "0 24px 70px rgba(0,0,0,.35)", overflow: "hidden" }}>
						<div style={{ padding: "18px 22px", borderBottom: "1px solid #eef0f4", display: "flex", alignItems: "center", gap: 10 }}>
							<span style={{ width: 26, height: 26, borderRadius: 6, background: "#4f46e5", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>SF</span>
							<span style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: "#1a1e2e" }}>{phase === "done" ? "Pushed to Salesforce" : "Push to Salesforce"}</span>
							<button onClick={() => setModalOpen(false)} style={{ marginLeft: "auto", border: "none", background: "none", fontSize: 20, color: "#9aa0b4", cursor: "pointer", lineHeight: 1 }}>&times;</button>
						</div>
						<div style={{ padding: "20px 22px" }}>
							<div style={{ fontSize: 13, color: "#5f6478", lineHeight: 1.5, marginBottom: 16 }}>
								{phase === "done" ? "This traffic instruction was committed via the Salesforce traffic flow." : "This initiates the Salesforce traffic decision flow with the parsed instruction below. Review the steps before committing."}
							</div>
							{pushError && (
								<div style={{ padding: "10px 14px", borderRadius: 8, background: "#fdecef", border: "1px solid #f3ccd3", color: "#b73145", fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
									Error: {pushError}
								</div>
							)}
							{flowSteps.map((s, i) => (
								<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 11, padding: "9px 0", borderBottom: "1px solid #f5f6f9" }}>
									<span style={{ width: 22, height: 22, borderRadius: "50%", flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, background: s.badgeBg, color: s.badgeColor }}>{s.mark}</span>
									<div>
										<div style={{ fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1e2e" }}>{s.title}</div>
										<div style={{ fontSize: 12, color: "#8a8fa3", lineHeight: 1.4 }}>{s.detail}</div>
									</div>
								</div>
							))}
						</div>
						<div style={{ padding: "14px 22px", borderTop: "1px solid #eef0f4", display: "flex", gap: 10, background: "#fafbfd" }}>
							<button onClick={() => setModalOpen(false)} style={{ border: "1px solid #dcdfea", background: "#fff", color: "#333a52", borderRadius: 9, fontSize: 13, fontWeight: 700, padding: "10px 16px", cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>{phase === "done" ? "Close" : "Cancel"}</button>
							<button onClick={onConfirmPush} disabled={phase === "pushing"} style={{ marginLeft: "auto", border: "none", background: phase === "done" ? "#12996a" : phase === "pushing" ? "#9aa0b4" : "#4f46e5", color: "#fff", borderRadius: 9, fontSize: 13.5, fontWeight: 800, padding: "10px 20px", cursor: phase === "pushing" ? "default" : "pointer", fontFamily: "'Sora',sans-serif" }}>
								{phase === "done" ? "Done" : phase === "pushing" ? "Pushing..." : "Run flow & commit"}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Fullscreen PDF overlay */}
			{pdfFullscreen && pdfUrl && (
				<div
					onClick={() => setPdfFullscreen(false)}
					style={{ position: "fixed", inset: 0, background: "rgba(20,22,40,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 24 }}
				>
					<div onClick={(e) => e.stopPropagation()} style={{ width: "90vw", height: "90vh", background: "#e8e9ed", borderRadius: 14, boxShadow: "0 24px 70px rgba(0,0,0,.4)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
						<div style={{ flex: "none", display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: "1px solid #dcdee4", background: "#f5f5f8" }}>
							<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12, color: "#5f6478", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sel.pdfName || "PDF Preview"}</span>
							<a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ border: "1px solid #dcdfea", background: "#fff", color: "#4f46e5", borderRadius: 7, fontSize: 12, fontWeight: 600, padding: "5px 12px", textDecoration: "none", fontFamily: "'Sora',sans-serif" }}>
								&#8599; New tab
							</a>
							<button onClick={() => setPdfFullscreen(false)} style={{ border: "none", background: "none", fontSize: 22, color: "#9aa0b4", cursor: "pointer", lineHeight: 1, padding: "0 4px" }} title="Close">&times;</button>
						</div>
						<PdfCanvas
							pdfUrl={pdfUrl}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default TrafficInstructions;
